import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  PlusCircle, Save, Trash2, Sparkles, History, Package, FileText,
  CheckCircle2, AlertCircle, ChevronRight, Wand2, Repeat, Pause, Play, StopCircle, GitCompare, X, StickyNote, Loader2,
  GripVertical, LayoutGrid, RotateCcw, Pencil,
} from "lucide-react";
import { api, formatZAR, formatDate, MONTHS, todayISO } from "../api";
import { RepeatBadge } from "../components/StatusBadge";
import { toast } from "sonner";

const STATUS_LABEL = {
  draft: "Tasks being added",
  ready: "Ready for invoice number",
  numbered: "Invoice number assigned",
  on_hold: "On Hold (still outstanding)",
  ready_to_send: "Ready to send",
  sent: "Invoice sent",
  completed: "Completed",
  paused: "Paused",
};
const STATUS_TONE = {
  draft: "bg-[#FEF3C7] text-[#92400E] border-[#FBBF24]",
  ready: "bg-[#E5FFFF] text-[#0F4F51] border-[#00ACB3]",
  numbered: "bg-[#E5FFFF] text-[#0F4F51] border-[#00ACB3]",
  on_hold: "bg-[#FEF3C7] text-[#92400E] border-[#FBBF24]",
  ready_to_send: "bg-[#D1FAE5] text-[#065F46] border-[#34D399]",
  sent: "bg-[#D1FAE5] text-[#065F46] border-[#34D399]",
  completed: "bg-[#D1FAE5] text-[#065F46] border-[#34D399]",
  paused: "bg-[#EDE9FE] text-[#5B21B6] border-[#A78BFA]",
};
// Allowed forward transitions per current status
const NEXT_STATUSES = {
  draft: ["ready"],
  ready: ["numbered", "draft"],
  numbered: ["on_hold", "ready_to_send", "sent", "completed"],
  on_hold: ["ready_to_send", "sent", "completed", "numbered"],
  ready_to_send: ["sent", "on_hold"],
  sent: ["completed", "on_hold"],
  completed: ["sent"],
  paused: ["draft"],
};

export default function ClientWorkspace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const clientId = searchParams.get("client_id") || "";
  const now = new Date();
  const month = Number(searchParams.get("month") || now.getMonth() + 1);
  const year = Number(searchParams.get("year") || now.getFullYear());
  // End of range — when end == start, behaviour is identical to single-month mode
  const endMonth = Number(searchParams.get("end_month") || month);
  const endYear = Number(searchParams.get("end_year") || year);
  const startIdx = year * 12 + (month - 1);
  const endIdx = endYear * 12 + (endMonth - 1);
  const isSingleMonth = startIdx === endIdx;
  const rangeMonths = Math.max(1, endIdx - startIdx + 1);
  const dateFrom = `${year}-${String(month).padStart(2, "0")}-01`;
  const lastDay = new Date(endYear, endMonth, 0).getDate();
  const dateTo = `${endYear}-${String(endMonth).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;

  const [clients, setClients] = useState([]);
  const [client, setClient] = useState(null);
  const [cats, setCats] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [invoice, setInvoice] = useState(null);
  const [popular, setPopular] = useState([]);
  const [missing, setMissing] = useState([]);
  const [bundles, setBundles] = useState([]);

  const [showInvoiceInput, setShowInvoiceInput] = useState(false);
  const [invoiceNumberInput, setInvoiceNumberInput] = useState("");
  const [invoiceStatusChoice, setInvoiceStatusChoice] = useState("numbered");
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [showSplitInput, setShowSplitInput] = useState(false);
  const [splitNumberInput, setSplitNumberInput] = useState("");
  const [splitStatusChoice, setSplitStatusChoice] = useState("numbered");

  // V2: recurring templates
  const [templates, setTemplates] = useState([]);
  // V2: price-forward dialog state
  const [priceDialog, setPriceDialog] = useState(null); // { task, newPrice, oldPrice }
  // Force-remount key for the price inputs so Cancel reverts the visible value
  const [priceRevision, setPriceRevision] = useState(0);
  // V2: compare-months modal
  const [compareData, setCompareData] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  // Client sticky notes (per-client, visible across all months)
  const [notesDraft, setNotesDraft] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSavedAt, setNotesSavedAt] = useState(null);
  // Inline "Add tasks" bar inside the Tasks card
  const [showAddBar, setShowAddBar] = useState(false);
  // Invoice Preview modal (read-only clean view of the current period)
  const [showPreview, setShowPreview] = useState(false);

  // Section reordering (drag & drop, persisted to localStorage).
  // "schedules" used to be in the default order — it's removed now, but any
  // legacy saved order that still contains "schedules" is filtered out on load.
  const DEFAULT_SECTION_ORDER = ["history", "tasks", "invoice", "templates", "cards", "totals"];
  const [sectionOrder, setSectionOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("ws-section-order") || "null");
      if (Array.isArray(saved)) {
        // Already current: contains every default key.
        if (saved.length === DEFAULT_SECTION_ORDER.length
            && DEFAULT_SECTION_ORDER.every(k => saved.includes(k))) {
          return saved;
        }
        // Migration: drop unknown keys, then append any default keys that are
        // missing. "schedules" specifically lands just before "tasks".
        const known = saved.filter(k => DEFAULT_SECTION_ORDER.includes(k));
        DEFAULT_SECTION_ORDER.forEach(k => {
          if (!known.includes(k)) known.push(k);
        });
        // Persist the migrated order back to localStorage so consumers reading
        // the key see the up-to-date value without waiting for a manual drag.
        try { localStorage.setItem("ws-section-order", JSON.stringify(known)); } catch { /* ignore */ }
        return known;
      }
    } catch { /* ignore */ }
    return DEFAULT_SECTION_ORDER;
  });
  const [layoutMode, setLayoutMode] = useState(false);
  const [dragOverId, setDragOverId] = useState(null);

  const persistOrder = (arr) => {
    try { localStorage.setItem("ws-section-order", JSON.stringify(arr)); } catch { /* ignore */ }
  };
  const moveSection = (sourceId, targetId) => {
    if (!sourceId || sourceId === targetId) return;
    setSectionOrder(prev => {
      const arr = [...prev];
      const from = arr.indexOf(sourceId);
      const to = arr.indexOf(targetId);
      if (from < 0 || to < 0) return prev;
      arr.splice(from, 1);
      arr.splice(to, 0, sourceId);
      persistOrder(arr);
      return arr;
    });
  };
  const resetSectionOrder = () => {
    setSectionOrder(DEFAULT_SECTION_ORDER);
    try { localStorage.removeItem("ws-section-order"); } catch { /* ignore */ }
    toast.success("Layout reset to default");
  };
  const sectionProps = (id) => ({
    style: { order: sectionOrder.indexOf(id) },
    "data-testid": `ws-section-${id}`,
    draggable: layoutMode,
    onDragStart: (e) => {
      if (!layoutMode) return;
      e.dataTransfer.setData("text/plain", id);
      e.dataTransfer.effectAllowed = "move";
    },
    onDragOver: (e) => {
      if (!layoutMode) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (dragOverId !== id) setDragOverId(id);
    },
    onDragLeave: () => {
      if (dragOverId === id) setDragOverId(null);
    },
    onDrop: (e) => {
      if (!layoutMode) return;
      e.preventDefault();
      const src = e.dataTransfer.getData("text/plain");
      moveSection(src, id);
      setDragOverId(null);
    },
    className: layoutMode
      ? `relative rounded-xl outline outline-2 outline-dashed ${dragOverId === id ? "outline-[#00ACB3] bg-[#E5FFFF]/60" : "outline-[#CCCCCC]"} cursor-grab active:cursor-grabbing mb-3 transition`
      : "",
  });

  useEffect(() => {
    api.get("/clients").then(r => setClients(r.data));
    api.get("/task-categories").then(r => setCats(r.data));
    api.get("/task-bundles").then(r => setBundles(r.data));
  }, []);

  useEffect(() => {
    if (!clientId) return;
    api.get(`/clients/${clientId}`).then(r => {
      setClient(r.data);
      setNotesDraft(r.data?.notes || "");
      setNotesSavedAt(null);
    });
  }, [clientId]);

  const updateTaskPrice = async (task, newPrice) => {
    const n = Number(newPrice);
    if (isNaN(n) || n === task.price) return;
    // Open the price-forward dialog so the user chooses scope
    setPriceDialog({ task, newPrice: n, oldPrice: task.price });
  };

  // Actually persist a price change with the chosen scope
  const applyPriceChange = async (scope) => {
    if (!priceDialog) return;
    const { task, newPrice } = priceDialog;
    try {
      if (scope === "month_only") {
        await api.patch(`/tasks/${task.id}`, { price: newPrice });
        toast.success("Price updated for this month only");
      } else {
        // 'forward' = update template + remembered + future uninvoiced tasks
        const r = await api.post(`/tasks/${task.id}/update-price-forward`, {
          new_price: newPrice,
          update_template: true,
          update_remembered: true,
          update_future_uninvoiced: true,
        });
        const extra = r.data?.future_tasks_updated > 0 ? ` (+${r.data.future_tasks_updated} future month${r.data.future_tasks_updated === 1 ? "" : "s"})` : "";
        toast.success(`Price updated going forward${extra}`);
      }
      setPriceDialog(null);
      setPriceRevision(r => r + 1);
      loadAll();
      loadTemplates();
    } catch {
      toast.error("Could not update price");
    }
  };

  const cancelPriceDialog = () => {
    // Revert the input back to old price by force-remounting the input
    setPriceDialog(null);
    setPriceRevision(r => r + 1);
  };

  const loadTemplates = async () => {
    if (!clientId) { setTemplates([]); return; }
    try {
      const r = await api.get(`/clients/${clientId}/recurring-templates`);
      setTemplates(r.data);
    } catch { /* non-fatal */ }
  };

  const setTemplateStatus = async (tpl, status) => {
    try {
      await api.patch(`/recurring-templates/${tpl.id}`, { status });
      toast.success(`${tpl.category_name}: ${status}`);
      loadTemplates();
    } catch {
      toast.error("Could not update template");
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete recurring template "${tpl.category_name}"? (Tasks already created stay; just stops auto-creation in future.)`)) return;
    try {
      await api.delete(`/recurring-templates/${tpl.id}`);
      toast.success("Template deleted");
      loadTemplates();
    } catch {
      toast.error("Could not delete");
    }
  };

  const openCompare = async () => {
    if (!clientId) return;
    try {
      const r = await api.get(`/clients/${clientId}/compare-months`, { params: { year, month } });
      setCompareData(r.data);
      setShowCompare(true);
    } catch {
      toast.error("Could not load comparison");
    }
  };

  const saveNotes = async () => {
    if (!clientId) return;
    if (notesDraft === (client?.notes || "")) return; // nothing changed
    setNotesSaving(true);
    try {
      const r = await api.patch(`/clients/${clientId}`, { notes: notesDraft });
      setClient(r.data);
      setNotesSavedAt(new Date());
    } catch {
      toast.error("Could not save notes");
    } finally {
      setNotesSaving(false);
    }
  };

  const ensureRecurringNow = async () => {
    if (!clientId) return;
    try {
      const r = await api.post(`/clients/${clientId}/ensure-recurring`, { year, month });
      if (r.data?.created_count > 0) {
        toast.success(`Added ${r.data.created_count} recurring task${r.data.created_count === 1 ? "" : "s"} from previous months`);
        loadAll();
      } else {
        toast.info("No recurring tasks to add — this month is already up to date");
      }
    } catch {
      toast.error("Could not apply recurring tasks");
    }
  };


  const [yearOverview, setYearOverview] = useState(null);
  // Refs for the Year Overview strip — used to auto-scroll the selected start
  // month into view whenever the date range changes, so the selected range is
  // always the visual centre of attention.
  const yearStripRef = useRef(null);
  const startTileRef = useRef(null);

  // Load 12-month overview for the current year (single API call)
  useEffect(() => {
    if (!clientId) { setYearOverview(null); return; }
    api.get(`/clients/${clientId}/year-overview`, { params: { year } })
      .then(r => setYearOverview(r.data))
      .catch(() => setYearOverview(null));
  }, [clientId, year, tasks]);

  // Auto-scroll the selected start-month tile into the centre of the Year
  // Overview strip whenever the range changes (or the strip data loads).
  // Uses scrollIntoView with inline:"center" so the selected month sits in
  // the middle of the visible strip and the surrounding range is easy to see.
  useEffect(() => {
    if (!yearOverview || !startTileRef.current) return;
    try {
      startTileRef.current.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    } catch { /* older browsers — ignore */ }
  }, [yearOverview, year, month, endYear, endMonth]);

  const loadAll = async () => {
    if (!clientId) return;
    // Auto-roll forward this client's monthly tasks (only in single-month mode
    // so multi-month range views never accidentally create tasks)
    if (isSingleMonth) {
      try {
        const ens = await api.post(`/clients/${clientId}/ensure-recurring`, { year, month });
        if (ens.data?.created_count > 0) {
          toast.success(`Auto-added ${ens.data.created_count} recurring task${ens.data.created_count === 1 ? "" : "s"}`);
        }
      } catch { /* non-fatal */ }
    }

    const tasksParams = isSingleMonth
      ? { client_id: clientId, year, month }
      : { client_id: clientId, date_from: dateFrom, date_to: dateTo };
    const [t, inv, pop, miss] = await Promise.all([
      api.get("/tasks", { params: tasksParams }),
      api.get("/invoices/by-period", { params: { client_id: clientId, year, month } }),
      api.get(`/clients/${clientId}/popular-tasks`),
      api.get(`/clients/${clientId}/missing-this-month`, { params: { year, month } }),
    ]);
    setTasks(t.data);
    setInvoice(inv.data);
    setPopular(pop.data);
    setMissing(miss.data);
    setInvoiceNumberInput(inv.data?.invoice_number || "");
  };
  useEffect(() => { loadAll(); loadTemplates(); /* eslint-disable-next-line */ }, [clientId, year, month, endYear, endMonth]);

  const catName = id => cats.find(c => c.id === id)?.name || "—";
  const catFor = id => cats.find(c => c.id === id);

  const totals = useMemo(() => {
    let invoiced = 0, outstanding = 0;
    tasks.forEach(t => {
      if (t.invoice_ref) invoiced += Number(t.price || 0);
      else outstanding += Number(t.price || 0);
    });
    return { invoiced, outstanding, total: invoiced + outstanding };
  }, [tasks]);

  // Group tasks by invoice number (blank invoices grouped under "__none__"
  // and rendered last). Each row carries its group total and a flag indicating
  // whether it is the first row of its group — used to render the merged
  // "Total" cell with rowSpan, mimicking the spreadsheet's merged-cell look.
  const groupedRows = useMemo(() => {
    const groups = new Map(); // invoiceKey -> { ref, tasks[], total }
    for (const t of tasks) {
      const ref = (t.invoice_ref || "").trim();
      const key = ref || "__none__";
      if (!groups.has(key)) groups.set(key, { ref, key, tasks: [], total: 0 });
      const g = groups.get(key);
      g.tasks.push(t);
      g.total += Number(t.price || 0);
    }
    // Order groups: real invoice numbers first (alphabetical), "__none__" last
    const ordered = [...groups.values()].sort((a, b) => {
      if (a.key === "__none__") return 1;
      if (b.key === "__none__") return -1;
      return a.ref.localeCompare(b.ref);
    });
    // Within each group, sort by date ascending
    ordered.forEach(g => g.tasks.sort((a, b) => (a.date || "").localeCompare(b.date || "")));
    // Flatten with row metadata. `rowIndex` is used for Excel-style zebra
    // banding — every other row gets a tinted background so the spreadsheet is
    // easy to scan. Merged cells (Total / Invoice via rowSpan) keep their own
    // colour-coded background and sit on top of the band underneath.
    const rows = [];
    let ri = 0;
    ordered.forEach((g, gi) => {
      g.tasks.forEach((t, i) => {
        rows.push({
          task: t,
          group: g,
          groupIndex: gi,
          rowIndex: ri,
          isFirstInGroup: i === 0,
          groupSize: g.tasks.length,
          groupTotal: g.total,
        });
        ri += 1;
      });
    });
    return rows;
  }, [tasks]);

  // Months (YYYY-MM) that contain a custom-schedule installment for this
  // client — used to give Year Overview tiles a distinctive background so the
  // user can spot at a glance which months are part of a fixed payment plan.
  const customScheduleMonthSet = useMemo(() => {
    const set = new Set();
    (templates || []).forEach(tpl => {
      if (tpl.repeat_type !== "custom") return;
      const N = Number(tpl.custom_total_occurrences || 0);
      const iv = Math.max(1, Number(tpl.custom_interval || 1));
      const ay = Number(tpl.custom_anchor_year);
      const am = Number(tpl.custom_anchor_month);
      if (!N || !ay || !am) return;
      // When the schedule has an upfront line invoiced on its own anchor month
      // (the "following_month" mode), the anchor itself is part of the plan.
      const upStatus = tpl.custom_upfront_status || "not_applicable";
      const upCounts = upStatus !== "not_applicable" && upStatus !== "waived";
      const upAmt = upCounts ? Number(tpl.custom_upfront_amount || (Number(tpl.custom_upfront_percent || 0) * Number(tpl.custom_quoted_total || 0) / 100) || 0) : 0;
      const repaymentStarts = tpl.custom_repayment_starts || "following_month";
      const upfrontOwnMonth = upAmt > 0 && repaymentStarts === "following_month";
      const repayOffset = upfrontOwnMonth ? 1 : 0;
      if (upfrontOwnMonth) {
        const idx = ay * 12 + am - 1;
        const y = Math.floor(idx / 12);
        const m = (idx % 12) + 1;
        set.add(`${y}-${String(m).padStart(2, "0")}`);
      }
      for (let i = 0; i < N; i++) {
        const idx = (ay * 12 + am - 1) + iv * (i + repayOffset);
        const y = Math.floor(idx / 12);
        const m = (idx % 12) + 1;
        set.add(`${y}-${String(m).padStart(2, "0")}`);
      }
    });
    return set;
  }, [templates]);

  // Generic inline edit helper for the row fields (date, invoice_ref, notes)
  const updateTaskField = async (task, field, value) => {
    if (value === (task[field] || "")) return;
    try {
      await api.patch(`/tasks/${task.id}`, { [field]: value });
      loadAll();
    } catch {
      toast.error(`Could not update ${field}`);
    }
  };

  const setParam = (k, v) => {
    const np = new URLSearchParams(searchParams);
    if (v) np.set(k, v); else np.delete(k);
    setSearchParams(np);
  };
  const setParams = (updates) => {
    const np = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") np.set(k, String(v));
      else np.delete(k);
    });
    setSearchParams(np);
  };

  // Quick-add a single suggested category
  const quickAdd = async (catId, presetPrice) => {
    const cat = catFor(catId);
    if (!cat) return;
    try {
      const sugg = await api.get("/pricing/suggested", { params: { client_id: clientId, task_category_id: catId } });
      const price = presetPrice != null ? presetPrice : sugg.data.price;
      const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(Math.min(new Date().getDate(), 28)).padStart(2, "0")}`;
      await api.post("/tasks", {
        client_id: clientId,
        task_category_id: catId,
        date: dateStr,
        repeat_type: sugg.data.default_repeat || cat.default_repeat || "monthly",
        price,
        invoice_ref: invoice?.invoice_number || "",
        notes: "",
      });
      toast.success(`Added ${cat.name}`);
      loadAll();
    } catch {
      toast.error("Could not add task");
    }
  };

  const applyBundle = async (bundleId) => {
    if (!clientId) return;
    const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(Math.min(new Date().getDate(), 28)).padStart(2, "0")}`;
    try {
      const r = await api.post(`/task-bundles/${bundleId}/apply`, {
        client_id: clientId,
        date: dateStr,
      });
      toast.success(`Added ${r.data.created_count} tasks from bundle`);
      loadAll();
    } catch {
      toast.error("Could not apply bundle");
    }
  };

  const setInvoiceStatus = async (status) => {
    if (!invoice) return;
    await api.patch(`/invoices/${invoice.id}`, { status });
    toast.success("Invoice updated");
    loadAll();
  };

  const assignInvoiceNumber = async () => {
    if (!invoice) return;
    const newNum = invoiceNumberInput.trim();
    if (!newNum) {
      toast.error("Enter the invoice number from your accounting system");
      return;
    }
    // Warn if overwriting an existing number
    const existingNum = invoice.invoice_number || "";
    if (existingNum && existingNum !== newNum) {
      const taskCount = tasks.filter(t => t.invoice_ref === existingNum).length;
      const msg = `${client?.name || "This client"} ${MONTHS[month - 1]} ${year} already has invoice ${existingNum} on ${taskCount} task${taskCount === 1 ? "" : "s"}.\n\nReplace ${existingNum} with ${newNum} on ALL ${tasks.length} task${tasks.length === 1 ? "" : "s"} for this month?`;
      if (!window.confirm(msg)) return;
    }
    try {
      await api.post(`/invoices/${invoice.id}/assign-number`, {
        invoice_number: newNum,
        status: invoiceStatusChoice,
      });
      const statusLabel = invoiceStatusChoice === "on_hold" ? "On Hold (still outstanding)" : "Numbered";
      toast.success(`Invoice ${newNum} saved as ${statusLabel} on ${tasks.length} task${tasks.length === 1 ? "" : "s"}`);
      setShowInvoiceInput(false);
      loadAll();
    } catch (e) {
      toast.error("Could not save invoice number");
    }
  };

  const toggleTaskSelected = (taskId) => {
    setSelectedTaskIds(s => s.includes(taskId) ? s.filter(x => x !== taskId) : [...s, taskId]);
  };

  const clearSelection = () => { setSelectedTaskIds([]); setShowSplitInput(false); setSplitNumberInput(""); };

  const applySplitNumber = async () => {
    if (!invoice) return;
    const num = splitNumberInput.trim();
    if (!num) { toast.error("Enter the invoice number from your accounting system"); return; }
    if (selectedTaskIds.length === 0) { toast.error("Select at least one task"); return; }
    try {
      await api.post(`/invoices/${invoice.id}/assign-number-to-tasks`, {
        invoice_number: num,
        task_ids: selectedTaskIds,
        status: splitStatusChoice,
      });
      const statusLabel = splitStatusChoice === "on_hold" ? "On Hold (still outstanding)" : "Numbered";
      toast.success(`Invoice ${num} saved (${statusLabel}) on ${selectedTaskIds.length} task${selectedTaskIds.length === 1 ? "" : "s"}`);
      clearSelection();
      loadAll();
    } catch {
      toast.error("Could not save split invoice number");
    }
  };

  const deleteTask = async (t) => {
    const catLabel = catName(t.task_category_id);
    const activeTpl = templates.find(
      tpl => tpl.task_category_id === t.task_category_id && tpl.status === "active"
    );
    const msg = activeTpl
      ? `Delete "${catLabel}" from ${MONTHS[month - 1]} ${year}?\n\n` +
        `This client has an ACTIVE recurring template for "${catLabel}". ` +
        `Deleting will skip just this month — future months will still auto-create normally.\n\n` +
        `To stop the recurring entirely, use the Pause or Stop button on the Recurring templates panel.`
      : `Delete "${catLabel}" from ${MONTHS[month - 1]} ${year}?`;
    if (!window.confirm(msg)) return;
    try {
      const r = await api.delete(`/tasks/${t.id}`);
      if (r.data?.template_skipped) {
        toast.success(`Deleted ${catLabel} · template will skip this month`);
      } else {
        toast.success(`Deleted ${catLabel}`);
      }
      await reloadTasksOnly();
      if (activeTpl) loadTemplates();
    } catch {
      toast.error("Could not delete task");
    }
  };

  // Bulk delete of selected tasks (with active-template safeguard)
  const bulkDeleteSelected = async () => {
    if (selectedTaskIds.length === 0) return;
    const selected = tasks.filter(t => selectedTaskIds.includes(t.id));
    const linked = selected.filter(t =>
      templates.some(tpl => tpl.task_category_id === t.task_category_id && tpl.status === "active")
    );
    const baseMsg =
      `Delete ${selectedTaskIds.length} task${selectedTaskIds.length === 1 ? "" : "s"} from ${MONTHS[month - 1]} ${year}?\n\n`;
    let action = "this_month";
    if (linked.length > 0) {
      const choice = window.prompt(
        baseMsg +
        `${linked.length} of them are linked to ACTIVE recurring templates.\n\n` +
        `Type one of:\n` +
        `  this  = delete this month only (templates skip this month, resume next month)\n` +
        `  pause = delete this month AND pause the matching templates\n` +
        `  stop  = delete this month AND stop the matching templates (disable forever)\n\n` +
        `Leave blank or Cancel to abort.`,
        "this"
      );
      if (!choice) return;
      const norm = choice.trim().toLowerCase();
      if (!["this", "pause", "stop"].includes(norm)) {
        toast.error("Cancelled — must type this, pause or stop");
        return;
      }
      action = norm === "this" ? "this_month" : norm;
    } else {
      if (!window.confirm(baseMsg + "This removes them from this invoice period only.")) return;
    }
    let deleted = 0;
    try {
      for (const t of selected) {
        await api.delete(`/tasks/${t.id}`);
        deleted++;
      }
      if (linked.length > 0 && action !== "this_month") {
        const newStatus = action === "pause" ? "paused" : "stopped";
        const seen = new Set();
        for (const t of linked) {
          const tpl = templates.find(p => p.task_category_id === t.task_category_id && p.status === "active");
          if (tpl && !seen.has(tpl.id)) {
            await api.patch(`/recurring-templates/${tpl.id}`, { status: newStatus });
            seen.add(tpl.id);
          }
        }
        toast.success(`Deleted ${deleted} task${deleted === 1 ? "" : "s"} · ${seen.size} template${seen.size === 1 ? "" : "s"} ${newStatus}`);
      } else {
        toast.success(`Deleted ${deleted} task${deleted === 1 ? "" : "s"}`);
      }
    } catch {
      toast.error("Some tasks could not be deleted");
    }
    clearSelection();
    await reloadTasksOnly();
    loadTemplates();
  };

  // Inline new-row helpers
  const addTaskInline = async (catId) => {
    if (!clientId || !catId) return;
    try {
      const r = await api.get(`/clients/${clientId}/quick-add-candidates`, { params: { year, month } });
      const cat = r.data.find(c => c.task_category_id === catId);
      if (!cat) { toast.error("Category not found"); return; }
      if (cat.already_added_this_month) {
        toast.error(`${cat.name} already added this month`);
        return;
      }
      // Use today's date if today falls within the selected month, otherwise the 1st.
      const today = new Date();
      const sameMonth = today.getFullYear() === year && (today.getMonth() + 1) === month;
      const dateStr = sameMonth
        ? today.toISOString().slice(0, 10)
        : `${year}-${String(month).padStart(2, "0")}-01`;
      await api.post("/tasks", {
        client_id: clientId,
        task_category_id: catId,
        date: dateStr,
        price: Number(cat.suggested_price || 0),
        repeat_type: cat.default_repeat || "monthly",
        invoice_ref: "",   // Brand-new task — start without invoice number
        notes: "",
      });
      toast.success(`Added ${cat.name}`);
      await reloadTasksOnly();
    } catch {
      toast.error("Could not add task");
    }
  };

  const createCategoryAndAdd = async ({ name, default_price, default_repeat, notes, custom }) => {
    if (!name?.trim()) { toast.error("Name is required"); return false; }
    try {
      // 1) Master category. Custom recurrences default the category to monthly so the dropdowns
      //    elsewhere look sane; the custom schedule lives on the recurring template.
      const repeatForCat = default_repeat === "custom" ? "monthly" : (default_repeat || "monthly");
      const quotedTotal = Number(default_price || 0);
      const occurrences = Math.max(1, Number(custom?.occurrences || 1));
      // Upfront math (custom only) — if the client has agreed to e.g. 50% upfront,
      // only the *balance* is split across the repayment occurrences.
      const upfrontStatus = custom?.upfront_status || "not_applicable";
      const upfrontCounts = upfrontStatus !== "not_applicable" && upfrontStatus !== "waived";
      let upfrontAmount = 0;
      let upfrontPercent = 0;
      if (default_repeat === "custom" && upfrontCounts && quotedTotal > 0) {
        if (custom?.upfront_amount && Number(custom.upfront_amount) > 0) {
          upfrontAmount = Number(custom.upfront_amount);
          upfrontPercent = Math.round((upfrontAmount / quotedTotal) * 10000) / 100;
        } else if (custom?.upfront_percent && Number(custom.upfront_percent) > 0) {
          upfrontPercent = Number(custom.upfront_percent);
          upfrontAmount = Math.round((quotedTotal * upfrontPercent / 100) * 100) / 100;
        }
      }
      const balanceForSplit = Math.max(0, quotedTotal - upfrontAmount);
      // For custom recurrences, the category default_price stores the per-month repayment
      const perMonth = default_repeat === "custom"
        ? Math.round((balanceForSplit / occurrences) * 100) / 100
        : quotedTotal;
      const r = await api.post("/task-categories", {
        name: name.trim(),
        default_price: perMonth,
        default_repeat: repeatForCat,
      });
      const cat = r.data;
      // Immediately add the new category to local state so `catName()` resolves
      // for the new task on the very next render (otherwise the task row shows
      // "—" until the user refreshes the page).
      setCats(prev => (prev.some(c => c.id === cat.id) ? prev : [...prev, cat]));
      // Decide which month to anchor the first task on
      const today = new Date();
      const sameMonth = today.getFullYear() === year && (today.getMonth() + 1) === month;
      const anchorY = default_repeat === "custom" ? Number(custom?.anchor_year || year) : year;
      const anchorM = default_repeat === "custom" ? Number(custom?.anchor_month || month) : month;
      const dateStr = (sameMonth && default_repeat !== "custom")
        ? today.toISOString().slice(0, 10)
        : `${anchorY}-${String(anchorM).padStart(2, "0")}-01`;
      // Build the first task's notes with the schedule context (custom only)
      let firstNotes = notes || "";
      const repaymentStarts = custom?.repayment_starts || "following_month";
      const upfrontOccupiesOwnMonth = default_repeat === "custom"
        && upfrontStatus !== "not_applicable" && upfrontStatus !== "waived"
        && upfrontAmount > 0 && repaymentStarts === "following_month";
      if (default_repeat === "custom") {
        const fmt = (n) => `R ${n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ").replace(".", ",")}`;
        const schedule = upfrontAmount > 0
          ? `Original quote ${fmt(quotedTotal)} · Upfront ${fmt(upfrontAmount)} (${upfrontPercent}%) · Balance ${fmt(balanceForSplit)} · Payment 1 of ${occurrences}`
          : `Original quote ${fmt(quotedTotal)} · Payment 1 of ${occurrences}`;
        firstNotes = firstNotes ? `${firstNotes}\n${schedule}` : schedule;
      }
      // Repayment 1 is created NOW only if it shares the anchor month. With
      // "following_month" mode we skip it — the user will see the repayment
      // appear automatically when they navigate to anchor + 1, via the
      // workspace's `ensure-recurring` call.
      if (!upfrontOccupiesOwnMonth) {
        await api.post("/tasks", {
          client_id: clientId,
          task_category_id: cat.id,
          date: dateStr,
          price: perMonth,
          repeat_type: default_repeat === "custom" ? "custom" : (default_repeat || "monthly"),
          invoice_ref: "",
          notes: firstNotes,
          installment_kind: default_repeat === "custom" ? "repayment" : null,
        });
      }
      // For custom + "included" upfront: also create the upfront line in the same month
      if (default_repeat === "custom" && upfrontStatus === "included" && upfrontAmount > 0) {
        const fmt = (n) => `R ${n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ").replace(".", ",")}`;
        await api.post("/tasks", {
          client_id: clientId,
          task_category_id: cat.id,
          date: dateStr,
          price: upfrontAmount,
          repeat_type: "custom",
          invoice_ref: "",
          notes: `Upfront payment (${upfrontPercent}%) on quote ${fmt(quotedTotal)}`,
          installment_kind: "upfront",
        });
      }
      // 2) Recurring template — required so the schedule survives across months
      if (default_repeat === "monthly") {
        await api.post("/recurring-templates", {
          client_id: clientId,
          task_category_id: cat.id,
          repeat_type: "monthly",
          price: perMonth,
          status: "active",
          notes: notes || "",
        });
      } else if (default_repeat === "annual") {
        await api.post("/recurring-templates", {
          client_id: clientId,
          task_category_id: cat.id,
          repeat_type: "annual",
          price: perMonth,
          status: "active",
          notes: notes || "",
          anniversary_month: anchorM,
          anniversary_day: 1,
        });
      } else if (default_repeat === "custom") {
        await api.post("/recurring-templates", {
          client_id: clientId,
          task_category_id: cat.id,
          repeat_type: "custom",
          price: perMonth,
          status: "active",
          notes: notes || "",
          custom_interval: Math.max(1, Number(custom?.interval || 1)),
          custom_total_occurrences: occurrences,
          custom_anchor_year: anchorY,
          custom_anchor_month: anchorM,
          custom_quoted_total: quotedTotal,
          custom_upfront_percent: upfrontPercent || null,
          custom_upfront_amount: upfrontAmount || null,
          custom_upfront_status: upfrontStatus,
          custom_repayment_starts: repaymentStarts,
        });
      }
      toast.success(`Created "${name}" and added to ${MONTHS[anchorM - 1]} ${anchorY}`);
      await reloadTasksOnly();
      loadTemplates();
      return true;
    } catch {
      toast.error("Could not create category");
      return false;
    }
  };

  // Update the invoice_ref on every task within an existing invoice group
  // (used by the merged Invoice cell — typing a new number renames the whole group)
  const updateInvoiceGroupRef = async (group, newRef) => {
    const trimmed = (newRef || "").trim();
    const oldRef = (group.ref || "").trim();
    if (trimmed === oldRef) return;
    if (oldRef && !trimmed) {
      if (!window.confirm(`Clear invoice number ${oldRef} from all ${group.tasks.length} tasks in this group?`)) return;
    } else if (oldRef && trimmed !== oldRef) {
      if (!window.confirm(`Change invoice number from ${oldRef} to ${trimmed} on all ${group.tasks.length} tasks?`)) return;
    }
    try {
      await Promise.all(group.tasks.map(t => api.patch(`/tasks/${t.id}`, { invoice_ref: trimmed })));
      toast.success(trimmed
        ? `Invoice ${trimmed} applied to ${group.tasks.length} task${group.tasks.length === 1 ? "" : "s"}`
        : `Cleared invoice from ${group.tasks.length} task${group.tasks.length === 1 ? "" : "s"}`);
      await reloadTasksOnly();
    } catch {
      toast.error("Could not update invoice group");
    }
  };

  // Refresh just the visible tasks + invoice without triggering ensure-recurring,
  // so deletions actually stick instead of being auto-recreated.
  const reloadTasksOnly = async () => {
    if (!clientId) return;
    try {
      const tasksParams = isSingleMonth
        ? { client_id: clientId, year, month }
        : { client_id: clientId, date_from: dateFrom, date_to: dateTo };
      const [t, inv, c] = await Promise.all([
        api.get("/tasks", { params: tasksParams }),
        api.get("/invoices/by-period", { params: { client_id: clientId, year, month } }),
        api.get("/task-categories"),
      ]);
      setTasks(t.data);
      setInvoice(inv.data);
      setCats(c.data);
    } catch { /* non-fatal */ }
  };

  if (!clientId) {
    return (
      <div className="p-6 md:p-10 max-w-4xl mx-auto">
        <h1 className="text-3xl font-extrabold text-[#011617] mb-2" style={{ fontFamily: "Manrope" }}>
          Pick a client
        </h1>
        <p className="text-[#595959] mb-6">Open a client to see their full monthly workspace.</p>
        <div className="np-card">
          <select
            data-testid="ws-client-select"
            onChange={e => setParam("client_id", e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg border border-[#CCCCCC] bg-white outline-none focus:border-[#00ACB3]"
            defaultValue=""
          >
            <option value="" disabled>Choose a client...</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name} ({c.business})</option>)}
          </select>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      {/* Invoice preview modal */}
      {showPreview && (
        <InvoicePreviewModal
          client={client}
          year={year}
          month={month}
          isSingleMonth={isSingleMonth}
          dateFrom={dateFrom}
          dateTo={dateTo}
          rangeMonths={rangeMonths}
          invoice={invoice}
          tasks={tasks}
          templates={templates}
          catName={catName}
          clientId={clientId}
          onUpdateTaskField={updateTaskField}
          onUpdateTaskPrice={updateTaskPrice}
          onDeleteTask={deleteTask}
          onDataChanged={() => { loadAll(); loadTemplates(); }}
          onCreateCategory={createCategoryAndAdd}
          onClose={() => setShowPreview(false)}
        />
      )}
      {/* Sticky header — date range + controls */}
      <div className="sticky top-0 z-30 -mx-6 md:-mx-10 px-6 md:px-10 py-3 mb-4 bg-[#F4F4F4]/95 backdrop-blur border-b border-[#CCCCCC]">
        <div className="np-label mb-2">Client workspace</div>
        <div className="flex flex-wrap items-end gap-3">
          {/* Date range picker */}
          <div className="bg-white border border-[#CCCCCC] rounded-lg p-2 inline-flex items-center gap-2" data-testid="ws-range-picker">
            <span className="text-[10px] uppercase tracking-wider text-[#595959] font-bold ml-1">Date range</span>
            <select data-testid="ws-month" value={month} onChange={e => setParams({ month: e.target.value })} className="px-2 py-1 rounded border border-[#CCCCCC] bg-white outline-none focus:border-[#00ACB3] text-sm font-semibold">
              {MONTHS.map((m, i) => <option key={`s${m}`} value={i + 1}>{m}</option>)}
            </select>
            <input data-testid="ws-year" type="number" value={year} onChange={e => setParams({ year: e.target.value })} className="w-20 px-2 py-1 rounded border border-[#CCCCCC] bg-white outline-none focus:border-[#00ACB3] text-sm font-semibold" />
            <span className="text-[#595959]">→</span>
            <select data-testid="ws-end-month" value={endMonth} onChange={e => setParams({ end_month: e.target.value })} className="px-2 py-1 rounded border border-[#CCCCCC] bg-white outline-none focus:border-[#00ACB3] text-sm font-semibold">
              {MONTHS.map((m, i) => <option key={`e${m}`} value={i + 1}>{m}</option>)}
            </select>
            <input data-testid="ws-end-year" type="number" value={endYear} onChange={e => setParams({ end_year: e.target.value })} className="w-20 px-2 py-1 rounded border border-[#CCCCCC] bg-white outline-none focus:border-[#00ACB3] text-sm font-semibold" />
            {!isSingleMonth && (
              <button
                data-testid="ws-range-reset"
                onClick={() => setParams({ end_month: month, end_year: year })}
                className="ml-1 text-xs text-[#595959] hover:text-[#011617] underline"
                title="Reset to single month"
              >Single</button>
            )}
          </div>

          <Link to={`/enter-work?client_id=${clientId}`} data-testid="ws-full-wizard-btn" className="bg-[#0F4F51] hover:bg-[#011617] text-white font-semibold rounded-lg px-4 py-2.5 inline-flex items-center gap-2">
            <PlusCircle className="w-4 h-4" /> Full wizard
          </Link>
          <button
            data-testid="ws-apply-recurring-btn"
            onClick={ensureRecurringNow}
            title={isSingleMonth ? `Roll recurring tasks into ${MONTHS[month - 1]} ${year}` : `Roll recurring tasks into ${MONTHS[month - 1]} ${year} (start of range)`}
            className="bg-[#00ACB3] hover:bg-[#0F4F51] text-white font-semibold rounded-lg px-4 py-2.5 inline-flex items-center gap-2"
          >
            <Wand2 className="w-4 h-4" /> Apply recurring
          </button>
          <button
            data-testid="ws-compare-btn"
            onClick={openCompare}
            title="Compare the start month to the previous month"
            className="bg-white hover:bg-[#E5FFFF] text-[#0F4F51] border border-[#0F4F51] font-semibold rounded-lg px-4 py-2.5 inline-flex items-center gap-2"
          >
            <GitCompare className="w-4 h-4" /> Compare months
          </button>
          <button
            data-testid="ws-layout-mode-btn"
            onClick={() => setLayoutMode(m => !m)}
            title="Drag sections to rearrange the page layout (saved to your browser)"
            className={`font-semibold rounded-lg px-4 py-2.5 inline-flex items-center gap-2 border ${
              layoutMode
                ? "bg-[#00ACB3] text-white border-[#00ACB3] hover:bg-[#0F4F51]"
                : "bg-white text-[#0F4F51] border-[#0F4F51] hover:bg-[#E5FFFF]"
            }`}
          >
            <LayoutGrid className="w-4 h-4" /> {layoutMode ? "Done arranging" : "Rearrange"}
          </button>
          {layoutMode && (
            <button
              data-testid="ws-layout-reset-btn"
              onClick={resetSectionOrder}
              title="Reset section order to default"
              className="font-semibold rounded-lg px-3 py-2.5 inline-flex items-center gap-2 bg-white text-[#595959] border border-[#CCCCCC] hover:text-[#011617] hover:border-[#011617]"
            >
              <RotateCcw className="w-4 h-4" /> Reset
            </button>
          )}
        </div>
      </div>

      {/* Reorderable sections (drag handles appear in Rearrange mode) */}
      <div className="flex flex-col" data-testid="ws-sections-wrapper">
        {/* 6-month overview timeline strip */}
        <div {...sectionProps("history")}>
          {layoutMode && <DragHandle label="Year overview" />}
          {yearOverview && (
            <div className="mb-6" data-testid="ws-year-overview">
              <div className="np-label mb-2">{year} overview {year === now.getFullYear() ? "(Current year)" : ""}</div>
              <div className="overflow-x-auto" ref={yearStripRef}>
                <div className="flex gap-2 min-w-max">
                  {yearOverview.months.map(h => {
                    const isCurrent = h.year === year && h.month === month && isSingleMonth;
                    const inRange = (h.year * 12 + h.month - 1) >= startIdx && (h.year * 12 + h.month - 1) <= endIdx;
                    const isStart = h.year === year && h.month === month;
                    const empty = h.task_count === 0;
                    const isCustom = customScheduleMonthSet.has(`${h.year}-${String(h.month).padStart(2, "0")}`);
                    return (
                      <button
                        key={`${h.year}-${h.month}`}
                        ref={isStart ? startTileRef : undefined}
                        data-testid={`ws-year-tile-${h.year}-${h.month}`}
                        data-custom-schedule={isCustom ? "yes" : "no"}
                        onClick={() => setParams({ year: h.year, month: h.month, end_year: h.year, end_month: h.month })}
                        className={`relative text-left rounded-lg px-3 py-2 border min-w-[140px] transition ${
                          isCurrent
                            ? "bg-[#00ACB3] text-white border-[#00ACB3] shadow"
                            : inRange
                              ? "bg-[#E5FFFF] text-[#011617] border-[#00ACB3]"
                              : isCustom
                                ? "bg-[#FFFBEB] text-[#011617] border-2 border-[#FBBF24] hover:border-[#D97706]"
                                : empty
                                  ? "bg-white text-[#011617] border-dashed border-[#CCCCCC] hover:border-[#0F4F51]"
                                  : "bg-white text-[#011617] border-[#CCCCCC] hover:border-[#0F4F51]"
                        }`}
                      >
                        {isCustom && !isCurrent && (
                          <span
                            data-testid={`ws-year-tile-custom-${h.year}-${h.month}`}
                            title="Custom payment schedule installment"
                            className="absolute top-1 right-1 text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FBBF24] text-[#7C2D12]"
                          >
                            Custom
                          </span>
                        )}
                        <div className={`text-[10px] font-bold uppercase tracking-wider ${isCurrent ? "text-white/80" : "text-[#595959]"}`}>
                          {MONTHS[h.month - 1].slice(0, 3)} {h.year}
                        </div>
                        <div className={`text-sm font-extrabold ${empty ? "opacity-50" : ""}`} style={{ fontFamily: "Manrope" }}>
                          {formatZAR(h.total)}
                        </div>
                        <div className={`text-[10px] mt-0.5 ${isCurrent ? "text-white/80" : "text-[#595959]"}`}>
                          {h.task_count} task{h.task_count === 1 ? "" : "s"}
                        </div>
                        <div className={`text-[10px] mt-0.5 font-bold ${
                          isCurrent ? "text-white" :
                          h.invoice_status === "sent" || h.invoice_status === "completed" ? "text-[#065F46]" :
                          h.invoice_status === "on_hold" ? "text-[#92400E]" :
                          h.invoice_status === "numbered" ? "text-[#0F4F51]" :
                          "text-[#595959]"
                        }`}>
                          {h.invoice_number ? `#${h.invoice_number}` : (empty ? "—" : h.invoice_status)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <p className="text-xs text-[#595959] mt-1">Click a month to jump to it. Click the same month again to reset the range to a single month. <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#FFFBEB] border-2 border-[#FBBF24]"></span> = month with a custom payment-schedule installment.</span></p>
            </div>
          )}
        </div>

        {/* Tasks table */}
        <div {...sectionProps("tasks")}>
          {layoutMode && <DragHandle label="Tasks table" />}

          <div className="np-card mb-6">
        <div className="flex items-start justify-between mb-3 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-[#011617] leading-tight" style={{ fontFamily: "Manrope" }} data-testid="ws-client-name">
              {client?.name || "—"}
            </h1>
            <p className="text-xs text-[#595959] mt-1">
              {client?.business && <span>{client.business} · </span>}
              Tasks for {isSingleMonth
                ? `${MONTHS[month - 1]} ${year}`
                : `${formatDate(dateFrom)} – ${formatDate(dateTo)}`}
              {!isSingleMonth && <span className="ml-1 inline-flex items-center gap-1 bg-[#E5FFFF] text-[#0F4F51] rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">{rangeMonths} months</span>}
              {isSingleMonth && invoice?.invoice_number && (
                <span> · <span className="text-[#0F4F51] font-bold">Invoice #{invoice.invoice_number}</span></span>
              )}
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-[#595959] font-bold">
              {isSingleMonth ? "Total" : `Total (${rangeMonths} months)`}
            </div>
            <div className="text-2xl font-extrabold text-[#011617]" style={{ fontFamily: "Manrope" }}>{formatZAR(totals.total)}</div>
          </div>
        </div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <span className="np-label">{tasks.length} total</span>
          <div className="flex items-center gap-2">
            <button
              data-testid="ws-preview-invoice-btn"
              onClick={() => setShowPreview(true)}
              className="font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1 text-sm border bg-white text-[#0F4F51] border-[#0F4F51] hover:bg-[#E5FFFF]"
            >
              <FileText className="w-4 h-4" /> Preview Invoice
            </button>
            <button
              data-testid="ws-add-tasks-btn"
              onClick={() => setShowAddBar(s => !s)}
              className={`font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1 text-sm border ${
                showAddBar
                  ? "bg-[#00ACB3] text-white border-[#00ACB3] hover:bg-[#0F4F51]"
                  : "bg-white text-[#0F4F51] border-[#0F4F51] hover:bg-[#E5FFFF]"
              }`}
            >
              <PlusCircle className="w-4 h-4" /> {showAddBar ? "Close" : "Add tasks"}
            </button>
          </div>
        </div>

        {/* Inline add bar — opens directly inside the Tasks card */}
        {showAddBar && (
          <QuickAddPanel
            clientId={clientId}
            year={year}
            month={month}
            embedded
            onAdded={() => { loadAll(); loadTemplates(); }}
            onClose={() => setShowAddBar(false)}
            onCreateCategory={createCategoryAndAdd}
          />
        )}

        {selectedTaskIds.length > 0 && (
          <div data-testid="ws-split-bar" className="mb-3 p-3 rounded-lg bg-[#E5FFFF] border-2 border-[#00ACB3] flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-semibold text-[#0F4F51]">
              {selectedTaskIds.length} task{selectedTaskIds.length === 1 ? "" : "s"} selected
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {!showSplitInput ? (
                <button data-testid="ws-split-start-btn" onClick={() => setShowSplitInput(true)} className="text-sm font-semibold bg-[#0F4F51] hover:bg-[#011617] text-white rounded-lg px-3 py-2 inline-flex items-center gap-1">
                  Apply invoice # to selected only
                </button>
              ) : (
                <>
                  <input
                    data-testid="ws-split-number-input"
                    autoFocus
                    value={splitNumberInput}
                    onChange={e => setSplitNumberInput(e.target.value)}
                    placeholder="Invoice # from your accounting system"
                    className="px-3 py-2 rounded-lg border border-[#CCCCCC] outline-none focus:border-[#00ACB3] w-60"
                  />
                  <select
                    data-testid="ws-split-status-choice"
                    value={splitStatusChoice}
                    onChange={(e) => setSplitStatusChoice(e.target.value)}
                    className="text-sm font-semibold border border-[#CCCCCC] bg-white rounded-lg px-3 py-2 focus:border-[#00ACB3] outline-none"
                    title="How should the parent invoice be tracked?"
                  >
                    <option value="numbered">Numbered (track normally)</option>
                    <option value="on_hold">On Hold (keep outstanding)</option>
                  </select>
                  <button data-testid="ws-split-apply-btn" onClick={applySplitNumber} className="bg-[#00ACB3] hover:bg-[#0F4F51] text-white font-semibold rounded-lg px-3 py-2 inline-flex items-center gap-1">
                    <Save className="w-4 h-4" /> Apply to {selectedTaskIds.length}
                  </button>
                </>
              )}
              <button
                data-testid="ws-bulk-delete-btn"
                onClick={bulkDeleteSelected}
                className="text-sm font-semibold bg-white hover:bg-[#FEE2E2] text-[#991B1B] border border-[#F87171] rounded-lg px-3 py-2 inline-flex items-center gap-1"
                title="Delete the selected tasks from this invoice period"
              >
                <Trash2 className="w-4 h-4" /> Delete {selectedTaskIds.length}
              </button>
              <button data-testid="ws-split-cancel-btn" onClick={clearSelection} className="text-sm text-[#595959] hover:text-[#011617] underline">
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="np-table-header">
              <tr>
                <th className="px-3 py-2.5 w-8">
                  <input
                    type="checkbox"
                    data-testid="ws-select-all"
                    checked={tasks.length > 0 && selectedTaskIds.length === tasks.length}
                    onChange={(e) => setSelectedTaskIds(e.target.checked ? tasks.map(t => t.id) : [])}
                  />
                </th>
                <th className="text-left px-3 py-2.5 w-32">Date</th>
                <th className="text-left px-3 py-2.5">Task</th>
                <th className="text-right px-3 py-2.5 w-28">Quote</th>
                <th className="text-right px-3 py-2.5 w-28">Price Inv</th>
                <th className="text-right px-3 py-2.5 w-36 bg-[#E5FFFF] text-[#0F4F51]">Total</th>
                <th className="text-left px-3 py-2.5 w-32">Invoice Nr</th>
                <th className="text-left px-3 py-2.5">Notes</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {tasks.length === 0 && (
                <tr><td colSpan={9} className="text-center text-[#595959] py-8">
                  No tasks yet. Use the suggestions below or the <Link to={`/enter-work?client_id=${clientId}`} className="text-[#00ACB3] underline">full wizard</Link>.
                </td></tr>
              )}
              {groupedRows.map(({ task: t, group, rowIndex, isFirstInGroup, groupSize, groupTotal }) => {
                const isMulti = groupSize > 1;
                const isNoInvoice = group.key === "__none__";
                const isSelected = selectedTaskIds.includes(t.id);
                // Excel-style zebra banding: alternate per row, every other row
                // gets a slight tint so the spreadsheet is easier to scan. The
                // merged Total/Invoice cells keep their own background.
                const bandClass = isSelected
                  ? "bg-[#E5FFFF]/70"
                  : rowIndex % 2 === 0
                    ? "bg-white"
                    : "bg-[#EEF4F6]";
                return (
                  <tr
                    key={t.id}
                    data-testid={`ws-task-row-${t.id}`}
                    data-invoice-group={group.key}
                    data-band={rowIndex % 2 === 0 ? "even" : "odd"}
                    className={`border-b border-[#E6EAEA] hover:bg-[#E5FFFF]/40 transition-colors ${bandClass}`}
                  >
                    <td className="px-3 py-2.5">
                      <input
                        type="checkbox"
                        data-testid={`ws-select-${t.id}`}
                        checked={selectedTaskIds.includes(t.id)}
                        onChange={() => toggleTaskSelected(t.id)}
                      />
                    </td>
                    {/* Date — inline editable */}
                    <td className="px-3 py-2.5">
                      <input
                        type="date"
                        data-testid={`ws-date-input-${t.id}`}
                        defaultValue={t.date}
                        onBlur={(e) => updateTaskField(t, "date", e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                        className="px-2 py-1 text-[#011617] bg-transparent border border-transparent hover:border-[#CCCCCC] focus:border-[#00ACB3] focus:bg-white rounded outline-none w-full"
                      />
                    </td>
                    {/* Task name + small repeat pill + (custom) remaining-occurrences badge */}
                    <td className="px-3 py-2.5 font-semibold text-[#011617]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{catName(t.task_category_id)}</span>
                        {/* Hide the blue "Custom" repeat pill — the yellow
                            "N of N · X left" badge below already conveys this
                            context, and stacking both made the row cluttered. */}
                        {t.repeat_type !== "custom" && <RepeatBadge repeat={t.repeat_type} />}
                        {/* Upfront-deposit line for a custom schedule. Stand-out
                            navy pill so it never gets confused with a monthly
                            repayment row. */}
                        {t.installment_kind === "upfront" && (
                          <span
                            data-testid={`ws-upfront-badge-${t.id}`}
                            className="text-[10px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#0F4F51] text-white"
                          >
                            Upfront
                          </span>
                        )}
                        {(() => {
                          // Only show the "N of N" repayment badge for repayment installments
                          if (t.repeat_type !== "custom" || t.installment_kind === "upfront") return null;
                          const tpl = templates.find(p =>
                            p.task_category_id === t.task_category_id &&
                            p.repeat_type === "custom"
                          );
                          if (!tpl || !tpl.custom_total_occurrences) return null;
                          const ay = Number(tpl.custom_anchor_year);
                          const am = Number(tpl.custom_anchor_month);
                          const iv = Math.max(1, Number(tpl.custom_interval || 1));
                          const total = Number(tpl.custom_total_occurrences);
                          // Repayments may start on the anchor month or the
                          // following month depending on the template setting.
                          const upStatus = tpl.custom_upfront_status || "not_applicable";
                          const upCounts = upStatus !== "not_applicable" && upStatus !== "waived";
                          const upAmt = upCounts ? Number(tpl.custom_upfront_amount || (Number(tpl.custom_upfront_percent || 0) * Number(tpl.custom_quoted_total || 0) / 100) || 0) : 0;
                          const repaymentStarts = tpl.custom_repayment_starts || "following_month";
                          const repayOffset = (upAmt > 0 && repaymentStarts === "following_month") ? 1 : 0;
                          const ty = Number((t.date || "").slice(0, 4));
                          const tm = Number((t.date || "").slice(5, 7));
                          const delta = (ty * 12 + tm - 1) - (ay * 12 + am - 1) - repayOffset * iv;
                          if (delta < 0 || delta % iv !== 0) return null;
                          const n = Math.floor(delta / iv) + 1; // 1-indexed
                          const remaining = Math.max(0, total - n);
                          const completed = n >= total;
                          return (
                            <span
                              data-testid={`ws-occ-badge-${t.id}`}
                              title={`Custom schedule: every ${iv} month${iv === 1 ? "" : "s"} for ${total} occurrence${total === 1 ? "" : "s"}, starting ${ay}-${String(am).padStart(2,"0")}`}
                              className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border ${
                                completed
                                  ? "bg-[#D1FAE5] text-[#065F46] border-[#34D399]"
                                  : "bg-[#FFFBEB] text-[#92400E] border-[#FBBF24]"
                              }`}
                            >
                              {completed ? `Final (${n} of ${total})` : `${n} of ${total} · ${remaining} left`}
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    {/* Quote — read-only original quoted amount.
                        For custom-schedule tasks (upfront or repayment) this
                        is the template's `custom_quoted_total`. For all other
                        tasks the column is intentionally left blank since the
                        per-line price IS the quoted amount. */}
                    <td
                      data-testid={`ws-quote-cell-${t.id}`}
                      className="px-3 py-2.5 text-right text-[#595959]"
                    >
                      {(() => {
                        if (t.repeat_type !== "custom") return <span className="text-[#CCCCCC]">—</span>;
                        const tpl = templates.find(p =>
                          p.task_category_id === t.task_category_id &&
                          p.repeat_type === "custom"
                        );
                        const q = Number(tpl?.custom_quoted_total || 0);
                        if (!q) return <span className="text-[#CCCCCC]">—</span>;
                        return <span className="font-semibold">{formatZAR(q)}</span>;
                      })()}
                    </td>
                    {/* Price Inv — actual amount being invoiced for this line. Inline-editable. */}
                    <td className="px-3 py-2.5 text-right">
                      <input
                        key={`${t.id}-${t.price}-${priceRevision}`}
                        type="number"
                        step="0.01"
                        defaultValue={t.price}
                        data-testid={`ws-price-input-${t.id}`}
                        onBlur={(e) => updateTaskPrice(t, e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                        className="w-24 px-2 py-1 text-right font-bold text-[#011617] bg-transparent border border-transparent hover:border-[#CCCCCC] focus:border-[#00ACB3] focus:bg-white rounded outline-none"
                      />
                    </td>
                    {/* Total — merged cell that spans the entire invoice group */}
                    {isFirstInGroup && (
                      <td
                        rowSpan={groupSize}
                        data-testid={`ws-group-total-${group.key}`}
                        className={`px-3 py-2.5 text-right align-middle border-l border-r border-[#E6EAEA] ${
                          isNoInvoice
                            ? "bg-[#FEF3C7] text-[#92400E]"
                            : isMulti
                              ? "bg-[#00ACB3]/10 text-[#0F4F51]"
                              : "text-[#595959]"
                        }`}
                      >
                        <div className={`${isMulti ? "text-base font-extrabold" : "text-sm font-bold"} font-mono leading-tight`}>
                          {formatZAR(groupTotal)}
                        </div>
                        <div className={`text-[10px] uppercase tracking-wider mt-0.5 ${
                          isNoInvoice ? "text-[#92400E]" : "text-[#0F4F51]/70"
                        }`}>
                          {isNoInvoice
                            ? "No invoice"
                            : `${groupSize} task${groupSize === 1 ? "" : "s"}`}
                        </div>
                      </td>
                    )}
                    {/* Invoice number cell.
                        - For "No invoice" group: one editable input per row (so user can split).
                        - For existing invoice groups (N005, B002, etc): merged via rowSpan, single editable input
                          that renames the whole group on change. To split a single task out, use the row's
                          checkbox + "Apply invoice # to selected" toolbar. */}
                    {isNoInvoice ? (
                      <td className="px-3 py-2.5">
                        <input
                          key={`${t.id}-inv-${t.invoice_ref || ""}`}
                          type="text"
                          data-testid={`ws-invoice-input-${t.id}`}
                          defaultValue={t.invoice_ref || ""}
                          placeholder="—"
                          onBlur={(e) => updateTaskField(t, "invoice_ref", e.target.value.trim())}
                          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                          className="w-full px-2 py-1 font-mono text-xs text-[#0F4F51] bg-transparent border border-transparent hover:border-[#CCCCCC] focus:border-[#00ACB3] focus:bg-white rounded outline-none"
                        />
                      </td>
                    ) : isFirstInGroup ? (
                      <td
                        rowSpan={groupSize}
                        data-testid={`ws-invoice-group-${group.key}`}
                        className={`px-3 py-2.5 align-middle ${isMulti ? "bg-[#00ACB3]/5" : ""}`}
                      >
                        <input
                          key={`grp-${group.key}-${group.ref}`}
                          type="text"
                          data-testid={`ws-invoice-group-input-${group.key}`}
                          defaultValue={group.ref}
                          onBlur={(e) => updateInvoiceGroupRef(group, e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                          className={`w-full px-2 py-1 font-mono ${isMulti ? "text-sm font-bold" : "text-xs"} text-[#0F4F51] bg-transparent border border-transparent hover:border-[#CCCCCC] focus:border-[#00ACB3] focus:bg-white rounded outline-none`}
                        />
                        {isMulti && (
                          <div className="text-[10px] uppercase tracking-wider text-[#0F4F51]/70 mt-0.5 px-2">
                            {groupSize} tasks
                          </div>
                        )}
                      </td>
                    ) : null}
                    {/* Notes — multi-line, expands to show full content */}
                    <td className="px-3 py-2.5 text-[#595959] align-top">
                      <textarea
                        key={`${t.id}-notes-${t.notes || ""}`}
                        rows={2}
                        data-testid={`ws-notes-input-${t.id}`}
                        defaultValue={t.notes || ""}
                        placeholder="Add notes…"
                        onBlur={(e) => updateTaskField(t, "notes", e.target.value)}
                        onKeyDown={(e) => {
                          // Ctrl/Cmd+Enter commits; plain Enter creates a new line
                          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) e.target.blur();
                          if (e.key === "Escape") e.target.blur();
                        }}
                        className="block w-full min-w-[180px] max-w-[280px] px-2 py-1 text-sm leading-snug bg-transparent border border-transparent hover:border-[#CCCCCC] focus:border-[#00ACB3] focus:bg-white rounded outline-none resize-y whitespace-pre-wrap break-words"
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <button data-testid={`ws-delete-task-${t.id}`} onClick={() => deleteTask(t)} className="text-[#991B1B] hover:text-[#7F1D1D]" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {/* Empty inline "click to add" row (Excel-style).
                  Acts as a shortcut to the SAME Add tasks panel as the top button. */}
              <InlineAddRow
                year={year}
                month={month}
                onOpen={() => {
                  setShowAddBar(true);
                  // Scroll the embedded panel into view after it mounts
                  setTimeout(() => {
                    document.querySelector('[data-testid="ws-quickadd-panel"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 100);
                }}
              />
            </tbody>
          </table>
        </div>
          </div>
        </div>

        {/* Invoice status bar */}
        <div {...sectionProps("invoice")}>
          {layoutMode && <DragHandle label="Invoice status" />}
          <div className="np-card mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <FileText className="w-5 h-5 text-[#0F4F51]" />
            <div>
              <div className="np-label">Invoice for {MONTHS[month - 1]} {year}</div>
              <div className="font-bold text-[#011617]" data-testid="ws-invoice-status-label">
                <span className="text-[#0F4F51]">{client?.name || "—"}</span>
                <span className="text-[#CCCCCC] mx-2">·</span>
                {invoice ? STATUS_LABEL[invoice.status] : "..."}
                {invoice?.invoice_number && (
                  <span className="ml-2 text-[#0F4F51] font-extrabold">#{invoice.invoice_number}</span>
                )}
              </div>
            </div>
            {invoice && (
              <span data-testid="ws-invoice-status-badge" className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wider border ${STATUS_TONE[invoice.status]}`}>
                {invoice.status}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {invoice && (invoice.status === "draft" || invoice.status === "ready") && !showInvoiceInput && (
              <button
                data-testid="ws-assign-number-btn"
                onClick={() => { setInvoiceStatusChoice("numbered"); setShowInvoiceInput(true); }}
                className="text-sm font-semibold bg-[#00ACB3] hover:bg-[#0F4F51] text-white rounded-lg px-3 py-2 inline-flex items-center gap-1"
              >
                Enter invoice number
              </button>
            )}
            {invoice && ["numbered", "on_hold", "ready_to_send", "sent", "completed"].includes(invoice.status) && !showInvoiceInput && (
              <select
                data-testid="ws-status-select"
                value={invoice.status}
                onChange={(e) => setInvoiceStatus(e.target.value)}
                className="text-sm font-semibold border border-[#CCCCCC] bg-white rounded-lg px-3 py-2 focus:border-[#00ACB3] outline-none"
              >
                {[invoice.status, ...(NEXT_STATUSES[invoice.status] || [])]
                  .filter((v, i, a) => a.indexOf(v) === i)
                  .map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            )}
            {invoice && (invoice.status === "numbered" || invoice.status === "on_hold") && !showInvoiceInput && (
              <button
                data-testid="ws-edit-number-btn"
                onClick={() => { setInvoiceStatusChoice(invoice.status); setShowInvoiceInput(true); }}
                className="text-sm text-[#595959] hover:text-[#011617] underline"
              >
                Change number
              </button>
            )}
            {showInvoiceInput && (
              <div className="flex flex-col gap-1" data-testid="ws-invoice-input-row">
                <div className="text-[10px] uppercase tracking-wider text-[#0F4F51] font-bold">
                  Saving for <span className="text-[#011617]">{client?.name}</span> · {MONTHS[month - 1]} {year}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    data-testid="ws-invoice-number-input"
                    value={invoiceNumberInput}
                    onChange={e => setInvoiceNumberInput(e.target.value)}
                    placeholder="Invoice # from your accounting system"
                    className="px-3 py-2 rounded-lg border border-[#CCCCCC] outline-none focus:border-[#00ACB3] w-64"
                  />
                  <select
                    data-testid="ws-invoice-status-choice"
                    value={invoiceStatusChoice}
                    onChange={(e) => setInvoiceStatusChoice(e.target.value)}
                    className="text-sm font-semibold border border-[#CCCCCC] bg-white rounded-lg px-3 py-2 focus:border-[#00ACB3] outline-none"
                    title="Choose how to track this invoice"
                  >
                    <option value="numbered">Numbered (track normally)</option>
                    <option value="on_hold">On Hold (keep outstanding)</option>
                  </select>
                  <button data-testid="ws-confirm-number-btn" onClick={assignInvoiceNumber} className="bg-[#00ACB3] hover:bg-[#0F4F51] text-white font-semibold rounded-lg px-3 py-2 inline-flex items-center gap-1">
                    <Save className="w-4 h-4" /> Apply to all
                  </button>
                  <button data-testid="ws-cancel-number-btn" onClick={() => setShowInvoiceInput(false)} className="text-sm text-[#595959] hover:text-[#011617] underline">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        {invoice?.status === "draft" && tasks.length > 0 && (
          <p className="mt-3 text-xs text-[#595959]">
            <Wand2 className="w-3.5 h-3.5 inline mr-1" />
            Once your accounting system generates the invoice number, enter it here and pick <strong>Numbered</strong> (normal) or <strong>On Hold</strong> (keep it visible on the outstanding list, e.g. waiting on client confirmation).
          </p>
        )}
          </div>
        </div>

        {/* Recurring templates panel */}
        <div {...sectionProps("templates")}>
          {layoutMode && <DragHandle label="Recurring templates" />}
          <div className="np-card mb-6" data-testid="ws-templates-panel">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h2 className="text-lg font-bold text-[#011617] inline-flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <Repeat className="w-4 h-4 text-[#00ACB3]" /> Recurring templates
            <span className="np-label">{templates.length}</span>
          </h2>
          <p className="text-xs text-[#595959] m-0">
            Each active template auto-creates a task each month. Pause to skip; Stop to disable.
          </p>
        </div>
        {templates.length === 0 ? (
          <p className="text-sm text-[#595959]">No recurring templates yet. Add a few tasks via the wizard or "Apply recurring" — they'll become templates automatically.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="np-table-header">
                <tr>
                  <th className="text-left px-3 py-2">Category</th>
                  <th className="text-left px-3 py-2">Repeat</th>
                  <th className="text-right px-3 py-2">Price</th>
                  <th className="text-left px-3 py-2">Notes</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2 w-56">Controls</th>
                </tr>
              </thead>
              <tbody>
                {templates.map(t => {
                  const isPaused = t.status === "paused";
                  const isStopped = t.status === "stopped";
                  const isActive = t.status === "active";
                  return (
                    <tr key={t.id} className="border-b border-[#CCCCCC]" data-testid={`tpl-row-${t.id}`}>
                      <td className="px-3 py-2 font-semibold text-[#011617]">{t.category_name}</td>
                      <td className="px-3 py-2"><RepeatBadge repeat={t.repeat_type} /></td>
                      <td className="px-3 py-2 text-right font-bold text-[#011617]">{formatZAR(t.price)}</td>
                      <td className="px-3 py-2 text-[#595959] max-w-[160px] truncate">{t.notes || "—"}</td>
                      <td className="px-3 py-2">
                        <span
                          data-testid={`tpl-status-${t.id}`}
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${
                            isActive ? "bg-[#D1FAE5] text-[#065F46] border-[#34D399]" :
                            isPaused ? "bg-[#FEF3C7] text-[#92400E] border-[#FBBF24]" :
                                       "bg-[#FEE2E2] text-[#991B1B] border-[#F87171]"
                          }`}
                        >
                          {t.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-1">
                          {isActive && (
                            <button
                              data-testid={`tpl-pause-${t.id}`}
                              onClick={() => setTemplateStatus(t, "paused")}
                              className="text-xs font-semibold bg-[#FEF3C7] text-[#92400E] hover:bg-[#FDE68A] rounded px-2 py-1 inline-flex items-center gap-1"
                              title="Pause: keeps the template but skips auto-creation"
                            >
                              <Pause className="w-3 h-3" /> Pause
                            </button>
                          )}
                          {(isPaused || isStopped) && (
                            <button
                              data-testid={`tpl-resume-${t.id}`}
                              onClick={() => setTemplateStatus(t, "active")}
                              className="text-xs font-semibold bg-[#D1FAE5] text-[#065F46] hover:bg-[#A7F3D0] rounded px-2 py-1 inline-flex items-center gap-1"
                              title="Resume: re-enable auto-creation"
                            >
                              <Play className="w-3 h-3" /> Resume
                            </button>
                          )}
                          {!isStopped && (
                            <button
                              data-testid={`tpl-stop-${t.id}`}
                              onClick={() => setTemplateStatus(t, "stopped")}
                              className="text-xs font-semibold bg-[#FEE2E2] text-[#991B1B] hover:bg-[#FECACA] rounded px-2 py-1 inline-flex items-center gap-1"
                              title="Stop: client no longer subscribed to this service"
                            >
                              <StopCircle className="w-3 h-3" /> Stop
                            </button>
                          )}
                          <button
                            data-testid={`tpl-delete-${t.id}`}
                            onClick={() => deleteTemplate(t)}
                            className="text-[#991B1B] hover:text-[#7F1D1D] p-1"
                            title="Delete template (keeps existing tasks)"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
        </div>

        {/* 4-column suggestion strip */}
        <div {...sectionProps("cards")}>
          {layoutMode && <DragHandle label="Suggestion cards" />}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6 mb-6">
        {/* Client sticky notes — visible no matter which month is selected */}
        <div className="np-card flex flex-col" data-testid="client-notes-card">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-[#011617] inline-flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
              <StickyNote className="w-4 h-4 text-[#F59E0B]" /> Client notes
            </h2>
            <span className="np-label">Sticky</span>
          </div>
          <p className="text-xs text-[#595959] mb-3">
            Always visible for {client?.name || "this client"}, no matter which month you're viewing. Saves on blur.
          </p>
          <textarea
            data-testid="client-notes-textarea"
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            onBlur={saveNotes}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) e.target.blur(); }}
            placeholder="e.g. Domain points from bushwakka.africa to bushwakka.co.za · Hosting renews 14 Aug · Invoice via email only"
            className="flex-1 min-h-[140px] w-full px-3 py-2 rounded-lg border border-[#CCCCCC] bg-[#FFFBEB] text-sm text-[#011617] resize-y outline-none focus:border-[#F59E0B] focus:bg-white"
          />
          <div className="mt-2 text-[11px] text-[#595959] inline-flex items-center gap-2 min-h-[16px]" data-testid="client-notes-status">
            {notesSaving && (<><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>)}
            {!notesSaving && notesSavedAt && (
              <><CheckCircle2 className="w-3 h-3 text-[#065F46]" /> Saved {notesSavedAt.toLocaleTimeString()}</>
            )}
            {!notesSaving && !notesSavedAt && client?.notes && (
              <><CheckCircle2 className="w-3 h-3 text-[#CCCCCC]" /> Loaded</>
            )}
          </div>
        </div>

        {/* Missing from previous month */}
        <div className="np-card">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-[#011617] inline-flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
              <History className="w-4 h-4 text-[#00ACB3]" /> From last month
            </h2>
            <span className="np-label">{missing.length}</span>
          </div>
          <p className="text-xs text-[#595959] mb-3">Billed last month but not yet here. Add with one click.</p>
          {missing.length === 0 ? (
            <p className="text-sm text-[#595959]">All caught up — nothing missing from last month.</p>
          ) : (
            <ul className="space-y-2">
              {missing.map(m => (
                <li key={m.task_category_id} className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#011617] truncate">{m.name}</div>
                    <div className="text-xs text-[#595959]">last: {formatZAR(m.previous_price)} on {formatDate(m.previous_date)}</div>
                  </div>
                  <button data-testid={`ws-add-missing-${m.task_category_id}`} onClick={() => quickAdd(m.task_category_id, m.previous_price)} className="text-xs font-bold uppercase tracking-wider bg-[#E5FFFF] text-[#0F4F51] hover:bg-[#00DDE6] hover:text-[#011617] rounded-full px-3 py-1.5 inline-flex items-center gap-1 shrink-0">
                    <PlusCircle className="w-3.5 h-3.5" /> Add
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Popular for this client */}
        <div className="np-card">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-[#011617] inline-flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
              <Sparkles className="w-4 h-4 text-[#00ACB3]" /> Popular for this client
            </h2>
            <span className="np-label">{popular.length}</span>
          </div>
          <p className="text-xs text-[#595959] mb-3">Tasks this client has had most often.</p>
          {popular.length > 1 && (
            <button
              data-testid="ws-add-all-popular"
              onClick={async () => {
                if (!window.confirm(`Add ${popular.slice(0, 8).length} popular tasks to ${MONTHS[month - 1]} ${year}? Existing tasks are skipped.`)) return;
                let added = 0;
                for (const p of popular.slice(0, 8)) {
                  const exists = tasks.some(t => t.task_category_id === p.task_category_id);
                  if (exists) continue;
                  await quickAdd(p.task_category_id, p.last_price);
                  added++;
                }
                toast.success(`Added ${added} task${added === 1 ? "" : "s"}`);
              }}
              className="mb-2 text-xs font-bold uppercase tracking-wider bg-[#0F4F51] text-white hover:bg-[#011617] rounded-full px-3 py-1.5 inline-flex items-center gap-1"
            >
              <PlusCircle className="w-3.5 h-3.5" /> Add all popular tasks
            </button>
          )}
          {popular.length === 0 ? (
            <p className="text-sm text-[#595959]">No history yet — once you've added a few tasks they'll show here.</p>
          ) : (
            <ul className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {popular.slice(0, 8).map(p => (
                <li key={p.task_category_id} className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#011617] truncate inline-flex items-center gap-2">
                      {p.name}
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#0F4F51] bg-[#E5FFFF] rounded-full px-1.5 py-0.5">×{p.count}</span>
                    </div>
                    <div className="text-xs text-[#595959]">{p.default_repeat} • {formatZAR(p.last_price)}</div>
                  </div>
                  <button data-testid={`ws-add-popular-${p.task_category_id}`} onClick={() => quickAdd(p.task_category_id, p.last_price)} className="text-xs font-bold uppercase tracking-wider bg-[#E5FFFF] text-[#0F4F51] hover:bg-[#00DDE6] hover:text-[#011617] rounded-full px-3 py-1.5 inline-flex items-center gap-1 shrink-0">
                    <PlusCircle className="w-3.5 h-3.5" /> Add
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Bundles */}
        <div className="np-card">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-[#011617] inline-flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
              <Package className="w-4 h-4 text-[#00ACB3]" /> Quick bundles
            </h2>
            <Link to="/bundles" className="text-xs text-[#00ACB3] hover:text-[#0F4F51] font-semibold">Manage</Link>
          </div>
          <p className="text-xs text-[#595959] mb-3">Add a set of tasks in one click.</p>
          <ul className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {bundles.map(b => (
              <li key={b.id} className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#011617] truncate">{b.name}</div>
                  <div className="text-xs text-[#595959]">{b.items?.length || 0} tasks</div>
                </div>
                <button data-testid={`ws-apply-bundle-${b.id}`} onClick={() => applyBundle(b.id)} className="text-xs font-bold uppercase tracking-wider bg-[#0F4F51] text-white hover:bg-[#011617] rounded-full px-3 py-1.5 inline-flex items-center gap-1 shrink-0">
                  <PlusCircle className="w-3.5 h-3.5" /> Apply
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
        </div>

        {/* Totals */}
        <div {...sectionProps("totals")}>
          {layoutMode && <DragHandle label="Totals" />}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="np-card">
          <div className="np-label mb-1">Invoiced</div>
          <div className="text-2xl font-extrabold text-[#065F46]" style={{ fontFamily: "Manrope" }}>{formatZAR(totals.invoiced)}</div>
        </div>
        <div className="np-card">
          <div className="np-label mb-1">Outstanding</div>
          <div className="text-2xl font-extrabold text-[#92400E]" style={{ fontFamily: "Manrope" }}>{formatZAR(totals.outstanding)}</div>
        </div>
        <div className="np-card">
          <div className="np-label mb-1">Month total</div>
          <div className="text-2xl font-extrabold text-[#0F4F51]" style={{ fontFamily: "Manrope" }}>{formatZAR(totals.total)}</div>
        </div>
      </div>
        </div>
      </div>
      {/* end reorderable sections */}

      {/* Price-edit choice dialog */}
      {priceDialog && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          data-testid="price-dialog"
          onClick={(e) => { if (e.target === e.currentTarget) cancelPriceDialog(); }}
        >
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-lg font-extrabold text-[#011617]" style={{ fontFamily: "Manrope" }}>
                Apply price change
              </h3>
              <button onClick={cancelPriceDialog} className="text-[#595959] hover:text-[#011617]" data-testid="price-dialog-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-[#595959] mb-4">
              <span className="font-semibold text-[#011617]">{catName(priceDialog.task.task_category_id)}</span>{" "}
              · from <span className="font-mono">{formatZAR(priceDialog.oldPrice)}</span>
              {" → "}
              <span className="font-mono font-bold text-[#0F4F51]">{formatZAR(priceDialog.newPrice)}</span>
            </p>
            <div className="space-y-2">
              <button
                data-testid="price-dialog-month-only"
                onClick={() => applyPriceChange("month_only")}
                className="w-full text-left p-3 rounded-lg border border-[#CCCCCC] hover:border-[#00ACB3] hover:bg-[#E5FFFF] transition"
              >
                <div className="font-bold text-[#011617]">This month only</div>
                <div className="text-xs text-[#595959]">Just adjust this one task. Future months and the template keep the old price.</div>
              </button>
              <button
                data-testid="price-dialog-forward"
                onClick={() => applyPriceChange("forward")}
                className="w-full text-left p-3 rounded-lg border-2 border-[#00ACB3] bg-[#E5FFFF] hover:bg-[#00DDE6] transition"
              >
                <div className="font-bold text-[#011617]">This month + going forward</div>
                <div className="text-xs text-[#595959]">Updates the recurring template, remembered price, and any future uninvoiced tasks for {client?.name}.</div>
              </button>
            </div>
            <div className="mt-4 text-right">
              <button
                onClick={cancelPriceDialog}
                className="text-sm text-[#595959] hover:text-[#011617] underline"
                data-testid="price-dialog-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compare-months modal */}
      {showCompare && compareData && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          data-testid="compare-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setShowCompare(false); }}
        >
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-extrabold text-[#011617]" style={{ fontFamily: "Manrope" }}>
                  {MONTHS[compareData.previous.month - 1]} {compareData.previous.year} vs {MONTHS[compareData.current.month - 1]} {compareData.current.year}
                </h3>
                <p className="text-sm text-[#595959] mt-1">
                  Delta:{" "}
                  <span className={`font-bold ${compareData.delta > 0 ? "text-[#065F46]" : compareData.delta < 0 ? "text-[#991B1B]" : "text-[#595959]"}`}>
                    {compareData.delta >= 0 ? "+" : ""}{formatZAR(compareData.delta)}
                  </span>
                  {"  "}
                  ({formatZAR(compareData.previous.total)} → {formatZAR(compareData.current.total)})
                </p>
              </div>
              <button onClick={() => setShowCompare(false)} className="text-[#595959] hover:text-[#011617]" data-testid="compare-close">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Added */}
            <CompareSection title="Added this month" tone="green" rows={compareData.added} formatZAR={formatZAR} testid="compare-added">
              {(r) => <span className="font-mono font-bold text-[#065F46]">+{formatZAR(r.price)}</span>}
            </CompareSection>
            {/* Removed */}
            <CompareSection title="Missing this month (was in previous)" tone="red" rows={compareData.removed} formatZAR={formatZAR} testid="compare-removed">
              {(r) => <span className="font-mono font-bold text-[#991B1B]">−{formatZAR(r.price)}</span>}
            </CompareSection>
            {/* Repriced */}
            <CompareSection title="Price changed" tone="amber" rows={compareData.repriced} formatZAR={formatZAR} testid="compare-repriced">
              {(r) => (
                <span className="font-mono text-xs">
                  <span className="text-[#595959] line-through">{formatZAR(r.previous_price)}</span>
                  {" → "}
                  <span className="font-bold text-[#0F4F51]">{formatZAR(r.price)}</span>
                  <span className={`ml-1 ${r.delta > 0 ? "text-[#065F46]" : "text-[#991B1B]"}`}>
                    ({r.delta >= 0 ? "+" : ""}{formatZAR(r.delta)})
                  </span>
                </span>
              )}
            </CompareSection>
            {/* Unchanged */}
            <CompareSection title="Unchanged" tone="gray" rows={compareData.unchanged} formatZAR={formatZAR} testid="compare-unchanged">
              {(r) => <span className="font-mono text-[#595959]">{formatZAR(r.price)}</span>}
            </CompareSection>
          </div>
        </div>
      )}
    </div>
  );
}

// Tiny click-only row at the bottom of the Tasks table. Acts as a shortcut
// that opens the SAME Add tasks panel (`QuickAddPanel`) as the top-right
// button — no parallel UI, so the workflow stays consistent.
function InlineAddRow({ year, month, onOpen }) {
  const dateStr = (() => {
    const today = new Date();
    const sameMonth = today.getFullYear() === year && (today.getMonth() + 1) === month;
    return sameMonth ? today.toISOString().slice(0, 10) : `${year}-${String(month).padStart(2, "0")}-01`;
  })();
  return (
    <tr
      data-testid="ws-inline-add-row"
      onClick={onOpen}
      className="cursor-pointer border-b-2 border-dashed border-[#00ACB3]/40 hover:bg-[#E5FFFF]/60 transition"
    >
      <td className="px-3 py-2.5"></td>
      <td className="px-3 py-2.5 text-[#0F4F51] font-semibold text-xs italic">{dateStr}</td>
      <td className="px-3 py-2.5 text-[#0F4F51] font-semibold inline-flex items-center gap-1.5">
        <PlusCircle className="w-4 h-4" /> Click here to add a task…
      </td>
      <td colSpan={6} className="px-3 py-2.5 text-xs text-[#595959] italic">
        Opens the Add tasks panel
      </td>
    </tr>
  );
}


function QuickAddPanel({ clientId, year, month, onAdded, embedded = false, onClose, onCreateCategory }) {
  const [tab, setTab] = useState("existing"); // "existing" | "new"
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState({}); // cat_id -> { price, repeat, notes, date }
  const [busy, setBusy] = useState(false);
  const [newCat, setNewCat] = useState({ name: "", default_price: "", default_repeat: "monthly", notes: "" });
  // Inline catalogue edit: which category is being edited + its draft values.
  const [editingCatId, setEditingCatId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: "", default_price: "", default_repeat: "monthly" });

  const startEditCategory = (c) => {
    setEditingCatId(c.task_category_id);
    setEditDraft({
      name: c.name,
      default_price: c.suggested_price,
      default_repeat: c.default_repeat || "monthly",
    });
  };
  const cancelEditCategory = () => {
    setEditingCatId(null);
    setEditDraft({ name: "", default_price: "", default_repeat: "monthly" });
  };
  const saveEditCategory = async (catId) => {
    if (!editDraft.name.trim()) { toast.error("Name is required"); return; }
    try {
      await api.patch(`/task-categories/${catId}`, {
        name: editDraft.name.trim(),
        default_price: Number(editDraft.default_price || 0),
        default_repeat: editDraft.default_repeat,
      });
      toast.success("Category updated");
      cancelEditCategory();
      reload();
      onAdded?.();
    } catch {
      toast.error("Could not update category");
    }
  };
  const deleteCategory = async (c) => {
    const msg = `Delete "${c.name}"? This removes the catalogue entry. Existing invoiced rows that used this category remain on past invoices, but it will no longer appear in the picker.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/task-categories/${c.task_category_id}`);
      toast.success(`Removed "${c.name}"`);
      reload();
      onAdded?.();
    } catch {
      toast.error("Could not delete category");
    }
  };

  const reload = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const r = await api.get(`/clients/${clientId}/quick-add-candidates`, { params: { year, month } });
      setCandidates(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [clientId, year, month]);

  const toggle = (cat) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[cat.task_category_id]) {
        delete next[cat.task_category_id];
      } else {
        next[cat.task_category_id] = {
          price: cat.suggested_price,
          repeat: cat.default_repeat || "monthly",
          notes: "",
          date: `${year}-${String(month).padStart(2, "0")}-01`,
        };
      }
      return next;
    });
  };

  const updateRow = (catId, field, value) => {
    setSelected(prev => ({ ...prev, [catId]: { ...prev[catId], [field]: value } }));
  };

  const clearAll = () => setSelected({});

  const addSelected = async () => {
    const entries = Object.entries(selected);
    if (entries.length === 0) return;
    setBusy(true);
    let created = 0, failed = 0;
    for (const [catId, row] of entries) {
      try {
        await api.post("/tasks", {
          client_id: clientId,
          task_category_id: catId,
          date: row.date,
          price: Number(row.price || 0),
          repeat_type: row.repeat,
          notes: row.notes || "",
        });
        created++;
      } catch {
        failed++;
      }
    }
    setBusy(false);
    if (created > 0) toast.success(`Added ${created} task${created === 1 ? "" : "s"}${failed ? ` (${failed} failed)` : ""}`);
    if (failed > 0 && created === 0) toast.error("Could not add tasks");
    setSelected({});
    onAdded?.();
    reload();
    if (embedded && created > 0) onClose?.();
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter(c => c.name.toLowerCase().includes(q));
  }, [candidates, search]);

  const selectedCount = Object.keys(selected).length;
  const selectedTotal = Object.values(selected).reduce((s, r) => s + Number(r.price || 0), 0);

  return (
    <div className={embedded ? "mb-4 p-3 rounded-lg bg-[#E5FFFF]/40 border border-[#00ACB3]/40" : "np-card mb-6"} data-testid="ws-quickadd-panel">
      {!embedded && (
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h2 className="text-xl font-bold text-[#011617] inline-flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <PlusCircle className="w-5 h-5 text-[#00ACB3]" /> Quick add tasks
          </h2>
          <p className="text-xs text-[#595959] m-0">
            Tick a task → adjust price / repeat / notes → click <strong>Add</strong>. No need to leave this screen.
          </p>
        </div>
      )}
      {embedded && (
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="inline-flex items-center gap-1 bg-white rounded-lg border border-[#CCCCCC] p-0.5" data-testid="addtasks-tabs">
            <button
              data-testid="addtasks-tab-new"
              onClick={() => setTab("new")}
              className={`px-3 py-1.5 rounded text-sm font-bold inline-flex items-center gap-1 ${
                tab === "new"
                  ? "bg-[#FFFBEB] text-[#92400E] border border-[#FBBF24]"
                  : "text-[#92400E] hover:bg-[#FFFBEB]"
              }`}
              title="Create a brand-new task or category"
            >
              <PlusCircle className="w-3.5 h-3.5" /> + Add new task
            </button>
            <button
              data-testid="addtasks-tab-existing"
              onClick={() => setTab("existing")}
              className={`px-3 py-1.5 rounded text-sm font-bold inline-flex items-center gap-1 ${
                tab === "existing"
                  ? "bg-[#E5FFFF] text-[#0F4F51] border border-[#00ACB3]"
                  : "text-[#0F4F51] hover:bg-[#E5FFFF]"
              }`}
            >
              <FileText className="w-3.5 h-3.5" /> Existing tasks
            </button>
          </div>
          <p className="text-xs text-[#0F4F51] m-0 font-semibold">
            {tab === "existing" ? "Pick one or more tasks. Each becomes a new row." : "Create a new task and add it to this period."}
          </p>
        </div>
      )}
      {embedded && tab === "new" && (
        <div className="space-y-2 bg-[#FFFBEB] rounded-lg border border-[#FBBF24] p-3 mb-3" data-testid="addtasks-new-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <label className="text-xs font-bold text-[#595959]">
              Task name
              <input
                data-testid="addtasks-new-name"
                autoFocus
                value={newCat.name}
                onChange={e => setNewCat(s => ({ ...s, name: e.target.value }))}
                placeholder="e.g. SEO Audit"
                className="block w-full mt-1 px-3 py-2 rounded-lg border border-[#CCCCCC] outline-none focus:border-[#00ACB3] text-sm font-normal text-[#011617]"
              />
            </label>
            <label className="text-xs font-bold text-[#595959]">
              {newCat.default_repeat === "custom" ? "Original quoted price (total)" : "Default price"}
              <input
                data-testid="addtasks-new-price"
                type="number"
                step="0.01"
                value={newCat.default_price}
                onChange={e => setNewCat(s => ({ ...s, default_price: e.target.value }))}
                placeholder="0.00"
                className="block w-full mt-1 px-3 py-2 rounded-lg border border-[#CCCCCC] outline-none focus:border-[#00ACB3] text-sm font-normal text-[#011617]"
              />
              {newCat.default_repeat === "custom" && Number(newCat.default_price) > 0 && Number(newCat.custom_occurrences ?? 6) > 0 && (
                <div className="text-[11px] text-[#0F4F51] mt-1 font-normal" data-testid="addtasks-per-month-preview">
                  Invoiced as <strong>R {(Number(newCat.default_price) / Math.max(1, Number(newCat.custom_occurrences ?? 6))).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ").replace(".", ",")}</strong> per month
                  {" "}for {newCat.custom_occurrences ?? 6} occurrences
                </div>
              )}
            </label>
            <label className="text-xs font-bold text-[#595959]">
              Default recurrence
              <select
                data-testid="addtasks-new-repeat"
                value={newCat.default_repeat}
                onChange={e => setNewCat(s => ({ ...s, default_repeat: e.target.value }))}
                className="block w-full mt-1 px-3 py-2 rounded-lg border border-[#CCCCCC] outline-none focus:border-[#00ACB3] text-sm font-normal text-[#011617]"
              >
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
                <option value="once_off">Once off</option>
                <option value="custom">Custom (limited schedule)</option>
              </select>
            </label>
            <label className="text-xs font-bold text-[#595959]">
              Notes (optional)
              <textarea
                data-testid="addtasks-new-notes"
                rows={2}
                value={newCat.notes}
                onChange={e => setNewCat(s => ({ ...s, notes: e.target.value }))}
                className="block w-full mt-1 px-3 py-2 rounded-lg border border-[#CCCCCC] outline-none focus:border-[#00ACB3] text-sm font-normal text-[#011617] resize-y"
              />
            </label>
          </div>
          {newCat.default_repeat === "custom" && (
            <div className="bg-white rounded-lg border border-[#FBBF24] p-3 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="addtasks-new-custom">
              <label className="text-xs font-bold text-[#92400E]">
                Interval (months)
                <input
                  data-testid="addtasks-new-custom-interval"
                  type="number" min={1}
                  value={newCat.custom_interval ?? 1}
                  onChange={e => setNewCat(s => ({ ...s, custom_interval: e.target.value }))}
                  className="block w-full mt-1 px-2 py-1.5 rounded border border-[#FBBF24] outline-none text-sm font-normal text-[#011617]"
                />
              </label>
              <label className="text-xs font-bold text-[#92400E]">
                Occurrences
                <input
                  data-testid="addtasks-new-custom-occurrences"
                  type="number" min={1}
                  value={newCat.custom_occurrences ?? 6}
                  onChange={e => setNewCat(s => ({ ...s, custom_occurrences: e.target.value }))}
                  className="block w-full mt-1 px-2 py-1.5 rounded border border-[#FBBF24] outline-none text-sm font-normal text-[#011617]"
                />
              </label>
              <label className="text-xs font-bold text-[#92400E]">
                Start month
                <select
                  data-testid="addtasks-new-custom-month"
                  value={newCat.custom_anchor_month ?? month}
                  onChange={e => setNewCat(s => ({ ...s, custom_anchor_month: Number(e.target.value) }))}
                  className="block w-full mt-1 px-2 py-1.5 rounded border border-[#FBBF24] outline-none text-sm font-normal text-[#011617]"
                >
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </label>
              <label className="text-xs font-bold text-[#92400E]">
                Start year
                <input
                  data-testid="addtasks-new-custom-year"
                  type="number"
                  value={newCat.custom_anchor_year ?? year}
                  onChange={e => setNewCat(s => ({ ...s, custom_anchor_year: Number(e.target.value) }))}
                  className="block w-full mt-1 px-2 py-1.5 rounded border border-[#FBBF24] outline-none text-sm font-normal text-[#011617]"
                />
              </label>
              {/* Upfront-payment fields — % is the easy entry; amount auto-fills below as a hint */}
              <label className="text-xs font-bold text-[#92400E]">
                Upfront %
                <input
                  data-testid="addtasks-new-custom-upfront-percent"
                  type="number" min={0} max={100} step="0.01"
                  placeholder="e.g. 50"
                  value={newCat.custom_upfront_percent ?? ""}
                  onChange={e => setNewCat(s => ({ ...s, custom_upfront_percent: e.target.value, custom_upfront_amount: "" }))}
                  className="block w-full mt-1 px-2 py-1.5 rounded border border-[#FBBF24] outline-none text-sm font-normal text-[#011617]"
                />
              </label>
              <label className="text-xs font-bold text-[#92400E]">
                Upfront amount (R)
                <input
                  data-testid="addtasks-new-custom-upfront-amount"
                  type="number" min={0} step="0.01"
                  placeholder="e.g. 42500"
                  value={newCat.custom_upfront_amount ?? ""}
                  onChange={e => setNewCat(s => ({ ...s, custom_upfront_amount: e.target.value, custom_upfront_percent: "" }))}
                  className="block w-full mt-1 px-2 py-1.5 rounded border border-[#FBBF24] outline-none text-sm font-normal text-[#011617]"
                />
              </label>
              <label className="text-xs font-bold text-[#92400E] col-span-2">
                Upfront status
                <select
                  data-testid="addtasks-new-custom-upfront-status"
                  value={newCat.custom_upfront_status ?? "not_applicable"}
                  onChange={e => setNewCat(s => ({ ...s, custom_upfront_status: e.target.value }))}
                  className="block w-full mt-1 px-2 py-1.5 rounded border border-[#FBBF24] outline-none text-sm font-normal text-[#011617]"
                >
                  <option value="not_applicable">No upfront payment</option>
                  <option value="included">Add upfront to this invoice</option>
                  <option value="already_invoiced">Upfront already invoiced</option>
                  <option value="not_invoiced">Upfront required but not invoiced yet</option>
                  <option value="waived">Upfront waived</option>
                </select>
              </label>
              <label className="text-xs font-bold text-[#92400E] col-span-2">
                Repayment starts
                <select
                  data-testid="addtasks-new-custom-repayment-starts"
                  value={newCat.custom_repayment_starts ?? "following_month"}
                  onChange={e => setNewCat(s => ({ ...s, custom_repayment_starts: e.target.value }))}
                  className="block w-full mt-1 px-2 py-1.5 rounded border border-[#FBBF24] outline-none text-sm font-normal text-[#011617]"
                >
                  <option value="following_month">Following month after upfront (recommended)</option>
                  <option value="same_month">Same month as upfront</option>
                </select>
              </label>
              {/* Live preview of the calc */}
              {(() => {
                const quote = Number(newCat.default_price || 0);
                const N = Math.max(1, Number(newCat.custom_occurrences ?? 6));
                const pct = Number(newCat.custom_upfront_percent || 0);
                const amtIn = Number(newCat.custom_upfront_amount || 0);
                const counted = (newCat.custom_upfront_status ?? "not_applicable") !== "not_applicable"
                  && (newCat.custom_upfront_status ?? "not_applicable") !== "waived";
                const upfront = counted ? (amtIn > 0 ? amtIn : (pct > 0 ? quote * pct / 100 : 0)) : 0;
                const balance = Math.max(0, quote - upfront);
                const perMonth = balance > 0 && N > 0 ? balance / N : 0;
                return (
                  <p className="col-span-2 md:col-span-4 text-[11px] text-[#92400E] bg-[#FFFBEB] rounded px-2 py-1.5">
                    Original quote <b>{formatZAR(quote)}</b> · Upfront <b>{formatZAR(upfront)}</b> ({pct ? `${pct}%` : amtIn > 0 ? `${((amtIn / quote) * 100 || 0).toFixed(2)}%` : "0%"}) · Balance <b>{formatZAR(balance)}</b> · {N} payments of <b>{formatZAR(perMonth)}</b>
                  </p>
                );
              })()}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              onClick={() => { setTab("existing"); setNewCat({ name: "", default_price: "", default_repeat: "monthly", notes: "" }); }}
              className="text-sm text-[#595959] hover:text-[#011617] underline"
              data-testid="addtasks-new-cancel"
            >Cancel</button>
            <button
              data-testid="addtasks-new-save"
              onClick={async () => {
                if (!newCat.name.trim()) { toast.error("Name is required"); return; }
                setBusy(true);
                const payload = {
                  name: newCat.name,
                  default_price: newCat.default_price,
                  default_repeat: newCat.default_repeat,
                  notes: newCat.notes,
                  custom: newCat.default_repeat === "custom" ? {
                    interval: newCat.custom_interval ?? 1,
                    occurrences: newCat.custom_occurrences ?? 6,
                    anchor_year: newCat.custom_anchor_year ?? year,
                    anchor_month: newCat.custom_anchor_month ?? month,
                    upfront_percent: newCat.custom_upfront_percent === "" || newCat.custom_upfront_percent == null ? null : Number(newCat.custom_upfront_percent),
                    upfront_amount: newCat.custom_upfront_amount === "" || newCat.custom_upfront_amount == null ? null : Number(newCat.custom_upfront_amount),
                    upfront_status: newCat.custom_upfront_status ?? "not_applicable",
                    repayment_starts: newCat.custom_repayment_starts ?? "following_month",
                  } : undefined,
                };
                const ok = onCreateCategory ? await onCreateCategory(payload) : false;
                setBusy(false);
                if (ok) {
                  setNewCat({ name: "", default_price: "", default_repeat: "monthly", notes: "" });
                  setTab("existing");
                  reload();
                  if (embedded) onClose?.();
                }
              }}
              disabled={busy || !newCat.name.trim()}
              className="bg-[#00ACB3] hover:bg-[#0F4F51] text-white font-semibold rounded-lg px-4 py-2 inline-flex items-center gap-2 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Create &amp; add to this period
            </button>
          </div>
        </div>
      )}
      {embedded && tab !== "existing" ? null : (
      <>
      {!embedded && (
        <p className="text-xs text-[#0F4F51] m-0 mb-2 font-semibold inline-flex items-center gap-1">
          <PlusCircle className="w-3.5 h-3.5" /> Pick one or more tasks below. Each becomes a new row in the table.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          data-testid="quickadd-search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search tasks (e.g. 'hosting', 'domain')…"
          className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-[#CCCCCC] outline-none focus:border-[#00ACB3]"
        />
        {selectedCount > 0 && (
          <>
            <div className="text-sm font-semibold text-[#0F4F51]">
              {selectedCount} selected · {formatZAR(selectedTotal)}
            </div>
            <button
              data-testid="quickadd-add-btn"
              onClick={addSelected}
              disabled={busy}
              className="bg-[#00ACB3] hover:bg-[#0F4F51] text-white font-semibold rounded-lg px-4 py-2 inline-flex items-center gap-2 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />}
              Add {selectedCount} task{selectedCount === 1 ? "" : "s"}
            </button>
            <button
              data-testid="quickadd-clear-btn"
              onClick={clearAll}
              className="text-sm text-[#595959] hover:text-[#011617] underline"
            >
              Clear
            </button>
          </>
        )}
      </div>

      {loading && candidates.length === 0 && (
        <p className="text-sm text-[#595959]">Loading…</p>
      )}
      <div className={embedded ? "overflow-x-auto max-h-[420px] overflow-y-auto border border-[#CCCCCC] rounded-lg bg-white" : "overflow-x-auto"}>
        <table className="w-full text-sm" data-testid="quickadd-table">
          <thead className="np-table-header">
            <tr>
              <th className="px-3 py-2 w-8"></th>
              <th className="text-left px-3 py-2">Task</th>
              <th className="text-right px-3 py-2 w-28">Price</th>
              <th className="text-left px-3 py-2 w-32">Repeat</th>
              <th className="text-left px-3 py-2 w-32">Date</th>
              <th className="text-left px-3 py-2">Notes</th>
              <th className="text-left px-3 py-2 w-32">Context</th>
              <th className="text-right px-3 py-2 w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && !loading && (
              <tr><td colSpan={8} className="text-center text-[#595959] py-4">No matching tasks.</td></tr>
            )}
            {filtered.map(c => {
              const isChecked = !!selected[c.task_category_id];
              const row = selected[c.task_category_id];
              const isAdded = c.already_added_this_month;
              const isEditing = editingCatId === c.task_category_id;
              if (isEditing) {
                // Inline edit row: replaces the normal row content while editing.
                return (
                  <tr
                    key={c.task_category_id}
                    data-testid={`quickadd-row-${c.task_category_id}-edit`}
                    className="border-b border-[#FBBF24] bg-[#FFFBEB]"
                  >
                    <td className="px-3 py-2">
                      <span className="text-[10px] font-extrabold uppercase tracking-wider text-[#92400E]">Edit</span>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        data-testid={`quickadd-edit-name-${c.task_category_id}`}
                        value={editDraft.name}
                        onChange={e => setEditDraft(s => ({ ...s, name: e.target.value }))}
                        className="w-full px-2 py-1 rounded border border-[#FBBF24] outline-none text-sm font-bold text-[#011617]"
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input
                        type="number"
                        step="0.01"
                        data-testid={`quickadd-edit-price-${c.task_category_id}`}
                        value={editDraft.default_price}
                        onChange={e => setEditDraft(s => ({ ...s, default_price: e.target.value }))}
                        className="w-24 px-2 py-1 text-right rounded border border-[#FBBF24] outline-none text-sm font-bold text-[#011617]"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        data-testid={`quickadd-edit-repeat-${c.task_category_id}`}
                        value={editDraft.default_repeat}
                        onChange={e => setEditDraft(s => ({ ...s, default_repeat: e.target.value }))}
                        className="w-full px-2 py-1 rounded border border-[#FBBF24] outline-none text-sm"
                      >
                        <option value="monthly">Monthly</option>
                        <option value="annual">Annual</option>
                        <option value="once_off">Once-off</option>
                      </select>
                    </td>
                    <td colSpan={3} className="px-3 py-2 text-xs text-[#92400E] italic">
                      Editing the catalogue entry · Date / Notes / Context apply per-task, not here.
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        data-testid={`quickadd-edit-save-${c.task_category_id}`}
                        onClick={() => saveEditCategory(c.task_category_id)}
                        className="text-[#0F4F51] hover:text-[#00ACB3] mr-2"
                        title="Save"
                      >
                        <CheckCircle2 className="w-4 h-4 inline" />
                      </button>
                      <button
                        type="button"
                        data-testid={`quickadd-edit-cancel-${c.task_category_id}`}
                        onClick={cancelEditCategory}
                        className="text-[#595959] hover:text-[#011617]"
                        title="Cancel"
                      >
                        <X className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                );
              }
              return (
                <tr
                  key={c.task_category_id}
                  data-testid={`quickadd-row-${c.task_category_id}`}
                  className={`border-b border-[#CCCCCC] ${isAdded ? "bg-[#E5FFFF]/40" : "hover:bg-[#E5FFFF]/30"} ${isChecked ? "bg-[#00ACB3]/10" : ""}`}
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      data-testid={`quickadd-check-${c.task_category_id}`}
                      checked={isChecked}
                      disabled={isAdded}
                      onChange={() => toggle(c)}
                      title={isAdded ? "Already added this month" : "Tick to add"}
                    />
                  </td>
                  <td className="px-3 py-2 font-semibold text-[#011617]">{c.name}</td>
                  <td className="px-3 py-2 text-right">
                    {isChecked ? (
                      <input
                        type="number"
                        step="0.01"
                        value={row.price}
                        onChange={e => updateRow(c.task_category_id, "price", e.target.value)}
                        className="w-24 px-2 py-1 text-right font-bold border border-[#00ACB3] rounded outline-none"
                      />
                    ) : (
                      <span className="font-mono text-[#0F4F51]">{formatZAR(c.suggested_price)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {isChecked ? (
                      <select
                        value={row.repeat}
                        onChange={e => updateRow(c.task_category_id, "repeat", e.target.value)}
                        className="px-2 py-1 rounded border border-[#00ACB3] text-xs font-semibold outline-none"
                      >
                        <option value="once_off">Once</option>
                        <option value="monthly">Monthly</option>
                        <option value="annual">Annual</option>
                      </select>
                    ) : (
                      <RepeatBadge repeat={c.default_repeat} />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {isChecked ? (
                      <input
                        type="date"
                        value={row.date}
                        onChange={e => updateRow(c.task_category_id, "date", e.target.value)}
                        className="px-2 py-1 rounded border border-[#00ACB3] outline-none w-full"
                      />
                    ) : (
                      <span className="text-xs text-[#595959]">{`${year}-${String(month).padStart(2, "0")}-01`}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {isChecked ? (
                      <input
                        type="text"
                        value={row.notes}
                        onChange={e => updateRow(c.task_category_id, "notes", e.target.value)}
                        placeholder="Optional notes"
                        className="w-full px-2 py-1 rounded border border-[#00ACB3] outline-none text-sm"
                      />
                    ) : (
                      <span className="text-xs text-[#595959]">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {isAdded && (
                      <span className="inline-flex items-center gap-1 text-[#065F46] font-semibold">
                        <CheckCircle2 className="w-3 h-3" /> Added
                      </span>
                    )}
                    {!isAdded && c.has_active_template && (
                      <span className="inline-flex items-center gap-1 text-[#0F4F51] font-semibold">
                        <Repeat className="w-3 h-3" /> Recurring
                      </span>
                    )}
                    {!isAdded && !c.has_active_template && c.usage_count > 0 && (
                      <span className="text-[#595959]">Used {c.usage_count}×</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      data-testid={`quickadd-edit-btn-${c.task_category_id}`}
                      onClick={() => startEditCategory(c)}
                      className="text-[#0F4F51] hover:text-[#00ACB3] mr-2"
                      title="Edit catalogue entry"
                    >
                      <Pencil className="w-4 h-4 inline" />
                    </button>
                    <button
                      type="button"
                      data-testid={`quickadd-delete-btn-${c.task_category_id}`}
                      onClick={() => deleteCategory(c)}
                      className="text-[#991B1B] hover:text-[#7F1D1D]"
                      title="Delete catalogue entry"
                    >
                      <Trash2 className="w-4 h-4 inline" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </>
      )}
    </div>
  );
}

function DragHandle({ label }) {
  return (
    <div className="absolute -top-3 left-3 z-20 inline-flex items-center gap-1.5 bg-[#00ACB3] text-white text-[10px] font-bold uppercase tracking-wider rounded-md px-2 py-1 pointer-events-none shadow">
      <GripVertical className="w-3 h-3" /> {label}
    </div>
  );
}

function CompareSection({ title, tone, rows, children, testid }) {
  const headerTone = {
    green: "text-[#065F46]",
    red: "text-[#991B1B]",
    amber: "text-[#92400E]",
    gray: "text-[#595959]",
  }[tone] || "text-[#011617]";
  return (
    <div className="mb-4" data-testid={testid}>
      <h4 className={`text-sm font-bold uppercase tracking-wider mb-2 ${headerTone}`}>
        {title} <span className="text-xs">({rows.length})</span>
      </h4>
      {rows.length === 0 ? (
        <p className="text-xs text-[#595959] italic pl-2">None</p>
      ) : (
        <ul className="border border-[#CCCCCC] rounded-lg divide-y divide-[#CCCCCC]">
          {rows.map(r => (
            <li key={r.task_id || r.task_category_id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
              <span className="font-semibold text-[#011617] truncate">{r.name}</span>
              <span>{children(r)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// Editable "preview invoice" modal — shows the current period as it will land
// on the invoice, AND lets the user fix mistakes without leaving the modal.
// All field edits auto-save on blur (via PATCH /tasks/{id} + PATCH /invoices/{id}),
// so no explicit Save button is required — Close simply dismisses.
function InvoicePreviewModal({
  client, year, month, isSingleMonth, dateFrom, dateTo, rangeMonths,
  invoice, tasks, templates, catName, clientId,
  onUpdateTaskField, onUpdateTaskPrice, onDeleteTask, onDataChanged, onCreateCategory,
  onClose,
}) {
  const [addingLine, setAddingLine] = useState(false);
  const [invNumberDraft, setInvNumberDraft] = useState(invoice?.invoice_number || "");
  useEffect(() => { setInvNumberDraft(invoice?.invoice_number || ""); }, [invoice?.invoice_number]);

  // Group tasks by invoice_ref so the grouped total still shows once per group.
  const grouped = useMemo(() => {
    const map = new Map();
    tasks.forEach(t => {
      const k = (t.invoice_ref || "").trim() || "__none__";
      if (!map.has(k)) map.set(k, { key: k, ref: t.invoice_ref || "", tasks: [], total: 0 });
      const g = map.get(k);
      g.tasks.push(t);
      g.total += Number(t.price || 0);
    });
    const ordered = [...map.values()].sort((a, b) => {
      const ad = a.tasks.reduce((min, t) => t.date < min ? t.date : min, a.tasks[0].date);
      const bd = b.tasks.reduce((min, t) => t.date < min ? t.date : min, b.tasks[0].date);
      return ad.localeCompare(bd);
    });
    const rows = [];
    ordered.forEach(g => {
      g.tasks.forEach((t, i) => {
        rows.push({ task: t, group: g, isFirstInGroup: i === 0, groupSize: g.tasks.length, groupTotal: g.total });
      });
    });
    return rows;
  }, [tasks]);

  const subtotal = useMemo(() => tasks.reduce((s, t) => s + Number(t.price || 0), 0), [tasks]);
  const periodLabel = isSingleMonth
    ? `${MONTHS[month - 1]} ${year}`
    : `${formatDate(dateFrom)} – ${formatDate(dateTo)} (${rangeMonths} months)`;
  const todayLabel = new Date().toISOString().slice(0, 10);
  const quoteFor = (t) => {
    if (t.repeat_type !== "custom") return null;
    const tpl = templates.find(p => p.task_category_id === t.task_category_id && p.repeat_type === "custom");
    return tpl?.custom_quoted_total ? Number(tpl.custom_quoted_total) : null;
  };

  // Save the invoice number when the input blurs. Uses the assign-number
  // endpoint so the invoice_ref propagates to the tasks in the same period.
  const saveInvoiceNumber = async () => {
    if (!invoice?.id) return;
    const trimmed = (invNumberDraft || "").trim();
    if (trimmed === (invoice.invoice_number || "")) return;
    try {
      if (trimmed) {
        await api.post(`/invoices/${invoice.id}/assign-number`, { invoice_number: trimmed });
      } else {
        await api.patch(`/invoices/${invoice.id}`, { invoice_number: "" });
      }
      toast.success("Invoice number saved");
      onDataChanged?.();
    } catch { toast.error("Could not save invoice number"); }
  };
  const saveInvoiceStatus = async (newStatus) => {
    if (!invoice?.id || newStatus === invoice.status) return;
    try {
      await api.patch(`/invoices/${invoice.id}`, { status: newStatus });
      toast.success("Status updated");
      onDataChanged?.();
    } catch { toast.error("Could not update status"); }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete "${catName(t.task_category_id)}" from this invoice?`)) return;
    await onDeleteTask(t);
  };

  const invoiceStatuses = ["draft", "ready", "numbered", "on_hold", "ready_to_send", "sent", "completed", "paused"];

  return (
    <div
      data-testid="ws-invoice-preview-modal"
      onClick={onClose}
      className="fixed inset-0 z-50 bg-[#011617]/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl my-6 print:my-0 print:rounded-none print:shadow-none print:max-w-none"
      >
        <div className="flex items-center justify-between gap-2 px-6 py-3 border-b border-[#CCCCCC] print:hidden">
          <div className="np-label inline-flex items-center gap-1.5 text-[#0F4F51]">
            <FileText className="w-3.5 h-3.5" /> Invoice preview <span className="text-[#595959] normal-case tracking-normal font-normal ml-1">· Edits save automatically</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="ws-invoice-preview-print-btn"
              onClick={() => window.print()}
              className="text-sm font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1 bg-white text-[#0F4F51] border border-[#0F4F51] hover:bg-[#E5FFFF]"
            >
              Print
            </button>
            <button
              type="button"
              data-testid="ws-invoice-preview-close-btn"
              onClick={onClose}
              className="text-sm font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1 bg-[#0F4F51] text-white hover:bg-[#00ACB3]"
            >
              <X className="w-4 h-4" /> Close
            </button>
          </div>
        </div>

        <div className="px-8 py-6 print:px-4">
          <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
            <div>
              <h2 className="text-2xl font-extrabold text-[#011617]" style={{ fontFamily: "Manrope" }}>{client?.name || "—"}</h2>
              {client?.business && <p className="text-sm text-[#595959]">{client.business}</p>}
              <p className="text-xs text-[#595959] mt-1">Billing period: <span className="font-semibold text-[#011617]">{periodLabel}</span></p>
              <p className="text-xs text-[#595959]">Generated: {todayLabel}</p>
            </div>
            <div className="text-right">
              <div className="np-label text-[#0F4F51]">Invoice Nr</div>
              <input
                type="text"
                data-testid="ws-invoice-preview-invoice-number"
                value={invNumberDraft}
                onChange={e => setInvNumberDraft(e.target.value)}
                onBlur={saveInvoiceNumber}
                onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                placeholder="No invoice number"
                className="text-lg font-extrabold text-[#011617] text-right bg-transparent border border-transparent hover:border-[#CCCCCC] focus:border-[#00ACB3] focus:bg-white rounded px-2 py-0.5 outline-none w-40 print:border-transparent"
                style={{ fontFamily: "Manrope" }}
              />
              <div className="np-label text-[#0F4F51] mt-1">Status</div>
              <select
                data-testid="ws-invoice-preview-status"
                value={invoice?.status || "draft"}
                onChange={e => saveInvoiceStatus(e.target.value)}
                className="text-[10px] font-extrabold uppercase tracking-wider text-[#92400E] bg-transparent border border-transparent hover:border-[#CCCCCC] focus:border-[#00ACB3] focus:bg-white rounded px-1 py-0.5 outline-none print:appearance-none"
              >
                {invoiceStatuses.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
              <div className="np-label text-[#0F4F51] mt-3">Total</div>
              <div className="text-2xl font-extrabold text-[#0F4F51]" data-testid="ws-invoice-preview-total" style={{ fontFamily: "Manrope" }}>{formatZAR(subtotal)}</div>
            </div>
          </div>

          <table className="w-full text-sm border border-[#CCCCCC]" data-testid="ws-invoice-preview-table">
            <thead className="bg-[#F6FAFB] border-b border-[#CCCCCC]">
              <tr>
                <th className="text-left px-3 py-2 w-32 text-[10px] uppercase tracking-wider text-[#595959]">Date</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#595959]">Task</th>
                <th className="text-right px-3 py-2 w-28 text-[10px] uppercase tracking-wider text-[#595959]">Quote</th>
                <th className="text-right px-3 py-2 w-28 text-[10px] uppercase tracking-wider text-[#595959]">Price Inv</th>
                <th className="text-right px-3 py-2 w-32 bg-[#E5FFFF] text-[10px] uppercase tracking-wider text-[#0F4F51]">Total</th>
                <th className="text-left px-3 py-2 w-28 text-[10px] uppercase tracking-wider text-[#595959]">Invoice Nr</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#595959]">Notes</th>
                <th className="w-10 print:hidden"></th>
              </tr>
            </thead>
            <tbody>
              {grouped.length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-[#595959] italic">No tasks in this period.</td></tr>
              )}
              {grouped.map(({ task: t, group, isFirstInGroup, groupSize, groupTotal }, idx) => {
                const isMulti = groupSize > 1;
                const isNoInvoice = group.key === "__none__";
                const band = idx % 2 === 0 ? "bg-white" : "bg-[#F6FAFB]";
                const q = quoteFor(t);
                return (
                  <tr key={t.id} data-testid={`ws-invoice-preview-row-${t.id}`} className={`border-b border-[#E6EAEA] ${band}`}>
                    <td className="px-3 py-2 align-top">
                      <input
                        type="date"
                        defaultValue={t.date}
                        data-testid={`ws-invoice-preview-date-${t.id}`}
                        onBlur={e => onUpdateTaskField(t, "date", e.target.value)}
                        className="bg-transparent border border-transparent hover:border-[#CCCCCC] focus:border-[#00ACB3] focus:bg-white rounded px-1 py-0.5 outline-none text-xs w-full print:border-transparent"
                      />
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="font-semibold text-[#011617]">{catName(t.task_category_id)}</div>
                      {t.installment_kind === "upfront" && (
                        <div className="text-[10px] font-extrabold uppercase tracking-wider text-[#0F4F51] mt-0.5">Upfront payment</div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top text-right text-[#595959]">
                      {q ? formatZAR(q) : <span className="text-[#CCCCCC]">—</span>}
                    </td>
                    <td className="px-3 py-2 align-top text-right">
                      <input
                        type="number"
                        step="0.01"
                        defaultValue={t.price}
                        key={`${t.id}-${t.price}`}
                        data-testid={`ws-invoice-preview-price-${t.id}`}
                        onBlur={e => onUpdateTaskPrice(t, e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                        className="w-24 px-2 py-1 text-right font-bold text-[#011617] bg-transparent border border-transparent hover:border-[#CCCCCC] focus:border-[#00ACB3] focus:bg-white rounded outline-none print:border-transparent"
                      />
                    </td>
                    {isFirstInGroup && (
                      <td rowSpan={groupSize} className={`px-3 py-2 align-middle text-right font-extrabold ${isNoInvoice ? "bg-[#FEF3C7] text-[#92400E]" : isMulti ? "bg-[#E5FFFF] text-[#0F4F51]" : "text-[#011617]"}`}>
                        {formatZAR(groupTotal)}
                        {isMulti && <div className="text-[9px] uppercase tracking-wider font-bold mt-0.5">{groupSize} tasks</div>}
                      </td>
                    )}
                    <td className="px-3 py-2 align-top">
                      <input
                        type="text"
                        defaultValue={t.invoice_ref || ""}
                        key={`invref-${t.id}-${t.invoice_ref || ""}`}
                        data-testid={`ws-invoice-preview-invref-${t.id}`}
                        onBlur={e => onUpdateTaskField(t, "invoice_ref", e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                        placeholder={isNoInvoice ? "No invoice" : ""}
                        className="w-full px-2 py-1 text-sm font-semibold text-[#0F4F51] bg-transparent border border-transparent hover:border-[#CCCCCC] focus:border-[#00ACB3] focus:bg-white rounded outline-none print:border-transparent"
                      />
                    </td>
                    <td className="px-3 py-2 align-top">
                      <textarea
                        defaultValue={t.notes || ""}
                        key={`notes-${t.id}-${t.notes || ""}`}
                        data-testid={`ws-invoice-preview-notes-${t.id}`}
                        onBlur={e => onUpdateTaskField(t, "notes", e.target.value)}
                        rows={2}
                        className="w-full px-2 py-1 text-xs text-[#595959] bg-transparent border border-transparent hover:border-[#CCCCCC] focus:border-[#00ACB3] focus:bg-white rounded outline-none resize-y print:border-transparent"
                      />
                    </td>
                    <td className="px-2 py-2 align-middle text-right print:hidden">
                      <button
                        type="button"
                        data-testid={`ws-invoice-preview-delete-${t.id}`}
                        onClick={() => handleDelete(t)}
                        className="text-[#991B1B] hover:text-[#7F1D1D]"
                        title="Delete this line"
                      >
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-[#F6FAFB] border-t-2 border-[#0F4F51]">
              <tr>
                <td colSpan={4} className="px-3 py-2 text-right font-bold text-[#595959] uppercase tracking-wider text-[10px]">Subtotal</td>
                <td className="px-3 py-2 text-right font-extrabold text-[#0F4F51]" data-testid="ws-invoice-preview-subtotal">{formatZAR(subtotal)}</td>
                <td colSpan={3}></td>
              </tr>
              <tr>
                <td colSpan={4} className="px-3 py-2 text-right font-extrabold text-[#011617] uppercase tracking-wider text-xs">Total</td>
                <td className="px-3 py-2 text-right font-extrabold text-[#0F4F51] text-base">{formatZAR(subtotal)}</td>
                <td colSpan={3}></td>
              </tr>
            </tfoot>
          </table>

          {/* Add line — reuses the same QuickAddPanel so the flow stays consistent */}
          <div className="mt-4 print:hidden">
            {!addingLine ? (
              <button
                type="button"
                data-testid="ws-invoice-preview-add-line-btn"
                onClick={() => setAddingLine(true)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-lg px-3 py-2 bg-[#00ACB3] text-white hover:bg-[#0F4F51]"
              >
                <PlusCircle className="w-4 h-4" /> Add line
              </button>
            ) : (
              <div className="border border-[#00ACB3] rounded-lg p-3 bg-[#E5FFFF]/30" data-testid="ws-invoice-preview-add-panel">
                <QuickAddPanel
                  clientId={clientId}
                  year={year}
                  month={month}
                  embedded
                  onAdded={() => { onDataChanged?.(); }}
                  onClose={() => setAddingLine(false)}
                  onCreateCategory={onCreateCategory}
                />
              </div>
            )}
          </div>

          {/* Custom-schedule context block (unchanged) */}
          {(() => {
            const customRows = tasks.filter(t => t.repeat_type === "custom");
            if (customRows.length === 0) return null;
            const seenTpls = new Set();
            const blocks = [];
            customRows.forEach(t => {
              const tpl = templates.find(p => p.task_category_id === t.task_category_id && p.repeat_type === "custom");
              if (!tpl || seenTpls.has(tpl.id)) return;
              seenTpls.add(tpl.id);
              const quoted = Number(tpl.custom_quoted_total || 0);
              const upStatus = tpl.custom_upfront_status || "not_applicable";
              const upCounts = upStatus !== "not_applicable" && upStatus !== "waived";
              const upAmt = upCounts ? Number(tpl.custom_upfront_amount || (Number(tpl.custom_upfront_percent || 0) * quoted / 100) || 0) : 0;
              const upPct = quoted > 0 ? Math.round((upAmt / quoted) * 10000) / 100 : 0;
              const balance = Math.max(0, quoted - upAmt);
              const N = Number(tpl.custom_total_occurrences || 0);
              blocks.push({ tpl, quoted, upAmt, upPct, balance, N, upStatus });
            });
            if (blocks.length === 0) return null;
            return (
              <div className="mt-5 border border-[#FBBF24] bg-[#FFFBEB] rounded-lg p-4" data-testid="ws-invoice-preview-custom-context">
                <div className="np-label text-[#92400E] mb-2">Custom payment schedule context</div>
                <p className="text-[11px] text-[#92400E] mb-2 italic">Editing Quote or Price Inv on custom-schedule rows changes only this invoice line. To adjust the whole payment schedule, use the Recurring Overview page.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  {blocks.map(b => (
                    <div key={b.tpl.id} className="bg-white rounded p-3 border border-[#FBBF24]">
                      <div className="font-extrabold text-[#011617]" style={{ fontFamily: "Manrope" }}>{catName(b.tpl.task_category_id)}</div>
                      <div className="text-[#595959] mt-1 leading-relaxed">
                        Original quote: <span className="font-bold text-[#011617]">{formatZAR(b.quoted)}</span><br />
                        {b.upAmt > 0 && (<>Upfront payment: <span className="font-bold text-[#011617]">{b.upPct}% / {formatZAR(b.upAmt)}</span><br /></>)}
                        Balance to repay: <span className="font-bold text-[#011617]">{formatZAR(b.balance)}</span><br />
                        Schedule: <span className="font-bold text-[#011617]">{b.N} monthly payments</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          <p className="text-[10px] text-[#595959] mt-6 italic">Preview only — capture the figures into Pastel or your accounting system.</p>
        </div>
      </div>
    </div>
  );
}
